import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiSend,
  FiShoppingCart,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiX,
  FiArrowLeft,
  FiArrowRight,
  FiPackage,
  FiNavigation,
  FiMapPin,
  FiCamera,
  FiImage,
  FiFileText,
  FiMic,
  FiSquare,
  FiSmile,
  FiPaperclip,
  FiHome,
  FiUserPlus,
  FiCornerUpLeft,
} from 'react-icons/fi';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import { getImagenUrl, getSchemaName } from 'shared/api';
import socket, { joinClienteRooms } from 'shared/socket';
import {
  FasePedidoCard,
  CambiosProductoCard,
  ImagenMessageCard,
  AudioMessageCard,
  DocumentoMessageCard,
} from './ChatCards';
import CampanaNotificaciones from 'shared/components/CampanaNotificaciones';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import { playNotificationSound } from 'shared/notificationSound';
import useWindowSize from 'shared/hooks/useWindowSize';
import { etiquetaTotal } from 'shared/etiquetaUtils';
import { firmaProductos } from 'shared/cartUtils';
import { fmtMonto } from 'shared/format';
import { formatHora, formatFecha } from './chat/utils/chatUtils';
import { animationsCSS, S } from './chat/ClienteChat.styles';
import SelectorPedidos from './SelectorPedidos';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MAX_RECEIVED_IDS = 500;

const ImagenProducto = React.memo(({ p, size, bg, icon }) => {
  const src = getImagenUrl(p.thumb_url) || getImagenUrl(p.imagen_url);
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: size === 36 ? 10 : 8,
        backgroundColor: bg || 'var(--color-bg-hover)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size > 32 ? 18 : 15,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </span>
      {src && (
        <img
          src={src}
          alt={p.nombre || ''}
          onError={(e) => e.target.remove()}
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
});

const RespuestaPreview = ({ respuesta }) => {
  if (!respuesta) return null;
  const autorLabel =
    respuesta.autor === 'cliente'
      ? 'Cliente'
      : respuesta.autor === 'asesor' || respuesta.autor === 'agente'
        ? 'Asesor'
        : respuesta.autor === 'bot'
          ? 'Bot'
          : 'Sistema';
  const esImagen = respuesta.tipo === 'imagen';
  const esAudio = respuesta.tipo === 'audio';
  const esDocumento = respuesta.tipo === 'documento';
  const texto = esImagen ? 'Imagen' : esAudio ? 'Audio' : esDocumento ? 'Documento' : respuesta.mensaje;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        borderLeft: '3px solid var(--color-secondary)',
        paddingLeft: '8px',
        marginBottom: '6px',
        fontSize: '12px',
        opacity: 0.9,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: '11px' }}>
        {esImagen ? '📷 ' : esAudio ? '🎤 ' : esDocumento ? '📄 ' : ''}
        {autorLabel}
      </span>
      <span
        style={{
          color: 'var(--color-text-muted)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {texto}
      </span>
    </div>
  );
};

const ESTADOS_ONBOARDING = [
  'NUEVO',
  'ESPERANDO_NOMBRE',
  'ESPERANDO_CEDULA',
  'ESPERANDO_TELEFONO',
  'ESPERANDO_UBICACION',
];

const estiloBurbuja = (msg, esContinuado) => {
  const esCliente = msg.autor === 'cliente';
  return {
    backgroundColor: esCliente ? 'var(--color-secondary)' : 'var(--color-surface)',
    color: esCliente ? 'var(--color-surface)' : 'var(--color-primary)',
    borderRadius: esContinuado
      ? '16px'
      : esCliente
        ? '16px 16px 5px 16px'
        : '16px 16px 16px 5px',
    boxShadow: esCliente ? '0 1px 3px rgba(26,115,232,0.3)' : '0 1px 2px rgba(0,0,0,0.08)',
  };
};

const BurbujaCola = ({ derecha, color }) => (
  <span
    style={{
      position: 'absolute',
      bottom: 0,
      ...(derecha ? { right: -4 } : { left: -4 }),
      width: 9,
      height: 9,
      backgroundColor: color,
      transform: 'rotate(45deg)',
    }}
  />
);

const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎',
  '🤔', '😅', '😢', '😭', '😡', '👍', '👎', '🙏',
  '👏', '💪', '🙌', '❤️', '🔥', '✅', '⚠️', '🎉',
];

function ClienteChat({
  config,
  sessionId: propSessionId,
  compact = false,
  onPedidoConfirmado = null,
  onBack,
  onVerPedido,
  onConfirmarPedido = null,
  brandName = 'Agorax',
  logoUrl = null,
  pedidosActivos = [],
  pedidoSeleccionadoId = null,
  onSeleccionarPedido = null,
  activo = false,
}) {
  const navigate = useNavigate();
  const schema_name = getSchemaName();
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [botEscribiendo, setBotEscribiendo] = useState(false);
  const [mostrarScrollBtn, setMostrarScrollBtn] = useState(false);
  const [conectado, setConectado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [estadoOnboarding, setEstadoOnboarding] = useState(null);
  const [carrito, setCarrito] = useState([]);
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [gpsModalOpen, setGpsModalOpen] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsObteniendo, setGpsObteniendo] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [direccionesChat, setDireccionesChat] = useState([]);
  const [cargandoDirsChat, setCargandoDirsChat] = useState(false);
  const [mostrarOpcionesGPS, setMostrarOpcionesGPS] = useState(false);
  const [sedeVencida, setSedeVencida] = useState(false);
  const [estadoPedido, setEstadoPedido] = useState(null);
  const [solicitandoAsesor, setSolicitandoAsesor] = useState(false);
  const { width: ww } = useWindowSize();
  const isMobile = ww < 768;
  const isDesktop = ww >= 768;
  const [atencionInfo, setAtencionInfo] = useState(null);
  const [imagenesPreview, setImagenesPreview] = useState([]);
  const [imagenModalOpen, setImagenModalOpen] = useState(false);
  const [imagenSeleccionadaIdx, setImagenSeleccionadaIdx] = useState(0);
  const [grabando, setGrabando] = useState(false);
  const [productoDetalle, setProductoDetalle] = useState(null);
  const [cargandoProductoDetalle, setCargandoProductoDetalle] = useState(false);
  const [sede, setSede] = useState('0101');
  const [respuestaA, setRespuestaA] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechRef = useRef(null);
  const transcripcionRef = useRef('');
  const fileInputRef = useRef(null);
  const camaraInputRef = useRef(null);
  const docInputRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const tempIdCounter = useRef(0);
  const receivedIdsRef = useRef(new Set());
  const { agregarNotificacion } = useNotificaciones();

  const carritoKeyBase = propSessionId ? `agorax_cart_${propSessionId}` : `agorax_cart_${schema_name}`;
  const pedidoKey = `agorax_pedido_activo_${schema_name}`;
  const pva = config?.permitir_venta_agotados || false;

  const carritoRef = useRef(carrito);
  carritoRef.current = carrito;
  const pedidoRef = useRef(null);
  const estadoPedidoRef = useRef(estadoPedido);
  estadoPedidoRef.current = estadoPedido;

  const cartKeyForChat = useCallback(
    (pid) => (pid ? `agorax_cart_${sessionId}_${pid}` : carritoKeyBase),
    [sessionId, carritoKeyBase]
  );

  // Persistir el carrito del pedido que se esta viendo (misma clave que usa el catalogo).
  useEffect(() => {
    if (!sessionId || !pedidoRef.current) return;
    localStorage.setItem(cartKeyForChat(pedidoRef.current), JSON.stringify(carrito));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrito, sessionId, cartKeyForChat]);

  const syncCartToPedido = useCallback(
    async (cart, pedidoId) => {
      if (!pedidoId) return;
      try {
        const total = cart.reduce((s, c) => {
          const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
          const extra = (c.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
          return s + base + (c.etiquetas_extra || extra);
        }, 0);
        const productos = cart.map((c) => ({
          nombre: c.nombre || '',
          codigo: c.codigo || '',
          precio: c.precio || 0,
          cantidad: c.cantidad || 1,
          pesable: c.pesable || false,
          subtotal: c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad,
          imagen_url: c.imagen_url || '',
          etiquetas: (c.etiquetas || []).map(e => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 })),
          etiquetas_extra: c.etiquetas_extra || 0,
          combo_id: c.combo_id || null,
          componentes: c.componentes || null,
        }));
        const res = await axios.put(`${API_URL}/pedidos/${pedidoId}/sync-cart`, {
          schema_name,
          productos,
          total,
        });
        localStorage.setItem(cartKeyForChat(pedidoId), JSON.stringify(cart));
        return res.data.status === 'success';
      } catch { console.error('Error cargando pedidos en cargarHistorial'); }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema_name, cartKeyForChat]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const pid = pedidoRef.current;
      if (esPedidoBloqueado(estadoPedidoRef.current)) return;
      if (pid && carritoRef.current.length > 0) {
        syncCartToPedido(carritoRef.current, parseInt(pid, 10));
      }
    }, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrito, syncCartToPedido]);

  useEffect(() => {
    return () => {
      const pid = pedidoRef.current;
      if (esPedidoBloqueado(estadoPedidoRef.current)) return;
      if (pid && carritoRef.current.length > 0) {
        const total = carritoRef.current.reduce((s, c) => {
          const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
          const extra = (c.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
          return s + base + (c.etiquetas_extra || extra);
        }, 0);
        const productos = carritoRef.current.map((c) => ({
          nombre: c.nombre || '',
          codigo: c.codigo || '',
          precio: c.precio || 0,
          cantidad: c.cantidad || 1,
          pesable: c.pesable || false,
          subtotal: c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad,
          imagen_url: c.imagen_url || '',
          etiquetas: (c.etiquetas || []).map(e => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 })),
          etiquetas_extra: c.etiquetas_extra || 0,
          combo_id: c.combo_id || null,
          componentes: c.componentes || null,
        }));
        fetch(`${API_URL}/pedidos/${pid}/sync-cart`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ schema_name, productos, total }),
          keepalive: true,
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSessionId(propSessionId);
  }, [propSessionId]);

  useEffect(() => {
    if (!sessionId || !schema_name) return;

    if (!socket.connected) socket.connect();

    const onConnect = () => {
      setConectado(true);
      axios
        .get(`${API_URL}/chat/cliente-por-session/${schema_name}/${sessionId}`)
        .then((r) => {
          if (r.data.status === 'success' && r.data.data?.id) {
            joinClienteRooms(socket, {
              idcliente: r.data.data.id,
              empresaId: r.data.data.empresa_id,
              sessionId,
            });
            if (r.data.data.sede) setSede(r.data.data.sede);
          }
        })
        .catch((e) => console.error('Error cargando cliente por session:', e?.message || e));
    };

    const onDisconnect = () => setConectado(false);

    const onNuevoMensaje = (data) => {
      const msgSchema = data.schema || data.schema_name;
      if (msgSchema && schema_name && msgSchema !== schema_name) return;
      const pid = pedidoRef.current;
      if (data.pedido_id && pid && parseInt(data.pedido_id) !== parseInt(pid)) return;
      if (data.id && receivedIdsRef.current.has(data.id)) return;
      setMensajes((prev) => {
        if (data.id && prev.some((m) => m.id === data.id)) return prev;
        if (
          data.autor === 'cliente' &&
          prev.some((m) => m.autor === 'cliente' && m.mensaje === data.mensaje)
        )
          return prev;
        if (data.id) {
          receivedIdsRef.current.add(data.id);
          if (receivedIdsRef.current.size > MAX_RECEIVED_IDS) {
            const arr = Array.from(receivedIdsRef.current);
            arr.slice(0, Math.floor(MAX_RECEIVED_IDS / 2)).forEach((id) => receivedIdsRef.current.delete(id));
          }
        }
        return [...prev, normalizarMensaje(data)];
      });
      if (data.autor !== 'asesor') {
        setBotEscribiendo(false);
      }
      requestAnimationFrame(() => scrollToBottom());

      if (data.autor === 'asesor') {
        mostrarNotificacion(data);
      }
    };

    const onPedidoActualizado = (data) => {
      const pid = localStorage.getItem(pedidoKey);
      if (pid && data.pedido_id === parseInt(pid, 10)) {
        if (data.estado) setEstadoPedido(data.estado);
        recargarCarrito();
      } else if (!pid && data.idcliente) {
        recargarCarrito();
      }
      if (data.confirmacion_pendiente) {
        agregarNotificacion({
          tipo: 'confirmacion',
          titulo: 'Confirma tu pedido',
          mensaje: `El asesor reviso tu pedido. Revisa los cambios y confirma.`,
          pedidoId: data.pedido_id,
        });
      }
    };

    const onAlertaStockBajo = (data) => {
    if (!pva) mostrarToast(`${data.producto} — quedan solo ${data.stock} und en ${data.sede}`, 'stock');
  };

    const onAtencionCambiada = (data) => {
      if (data.schema === schema_name && data.session_id === sessionId) {
        setAtencionInfo({
          atendido_por: data.atendido_por || null,
          nombre_asesor: data.nombre_asesor || null,
        });
        const ahora = new Date().toISOString();
        const msg = data.atendido_por
          ? `Ahora te atiende ${data.nombre_asesor || 'un asesor'}`
          : 'El asistente virtual continuara atendiendote';
        setMensajes((prev) => [
          ...prev,
          normalizarMensaje({
            id: `atencion_${Date.now()}`,
            mensaje: msg,
            tipo: 'sistema',
            hora: ahora,
            autor: 'sistema',
            idcliente: null,
          }),
        ]);
        requestAnimationFrame(() => scrollToBottom());
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('nuevo_mensaje', onNuevoMensaje);
    socket.on('pedido_actualizado', onPedidoActualizado);
    socket.on('alerta_stock_bajo', onAlertaStockBajo);
    socket.on('atencion_cambiada', onAtencionCambiada);

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('nuevo_mensaje', onNuevoMensaje);
      socket.off('pedido_actualizado', onPedidoActualizado);
      socket.off('alerta_stock_bajo', onAlertaStockBajo);
      socket.off('atencion_cambiada', onAtencionCambiada);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, schema_name]);

  useEffect(() => {
    if (!sessionId || !schema_name) return;
    cargarHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, schema_name, pedidoSeleccionadoId]);

  // Al volver a la pestana chat (el chat permanece montado con display:none), el
  // carrito local queda desactualizado respecto al catalogo, que escribe bajo la
  // misma clave por pedido. Re-hidratar aqui desde localStorage para sincronizar.
  useEffect(() => {
    if (!activo || !sessionId || !pedidoRef.current) return;
    const saved = localStorage.getItem(cartKeyForChat(pedidoRef.current));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (firmaProductos(parsed) !== firmaProductos(carritoRef.current)) {
          setCarrito(parsed);
          carritoRef.current = parsed;
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, sessionId, cartKeyForChat]);

  useEffect(() => {
    if (inputRef.current && !texto) {
      inputRef.current.style.height = 'auto';
    }
  }, [texto]);

  const normalizarMensaje = (data) => ({
    id: data.id,
    mensaje: data.mensaje,
    tipo: data.tipo || 'texto',
    accion: data.accion || data.tipo || 'texto',
    hora: data.hora,
    autor: data.autor,
    idcliente: data.idcliente,
    pedido_id: data.pedido_id,
    producto: data.producto || null,
    items_agregados: data.items_agregados || [],
    productos: data.productos || [],
    cantidad: data.cantidad,
    alternativas: data.alternativas,
    todas_opciones_keyword: data.todas_opciones_keyword || '',
    metadata: data.metadata || null,
    respuesta_a_id: data.respuesta_a_id || null,
    respuesta_a: data.respuesta_a || null,
  });

  const cargarHistorial = async () => {
    setCargando(true);

    // Detectar el pedido a mostrar: prioriza la seleccion del usuario (pestanas)
    // y cae al pedido activo mas reciente.
    let pedidoActivo = null;
    try {
      const pedRes = await axios.get(`${API_URL}/pedidos/mis-pedidos/${schema_name}/${sessionId}`);
      if (pedRes.data.status === 'success') {
        const activos = (pedRes.data.data || []).filter(
          (p) => !['entregado','rechazado','cancelado','credito_pagado'].includes(p.estado)
        );
        if (pedidoSeleccionadoId) {
          pedidoActivo = activos.find((p) => parseInt(p.id) === parseInt(pedidoSeleccionadoId));
        }
        if (!pedidoActivo) {
          const pidGuardado = localStorage.getItem(pedidoKey);
          if (pidGuardado) pedidoActivo = activos.find((p) => p.id === parseInt(pidGuardado, 10));
        }
        if (!pedidoActivo)
          pedidoActivo = activos.find((p) => p.session_id === sessionId) || activos[0] || null;
      }
    } catch {}

    // Sin pedido activo: reiniciar y pedir la direccion de entrega
    if (!pedidoActivo) {
      localStorage.removeItem(pedidoKey);
      localStorage.removeItem(carritoKeyBase);
      setCarrito([]);
      setMensajes([]);
      setEstadoPedido(null);
      if (config?.requiere_ubicacion_cliente === false) {
        setEstadoOnboarding('CONVERSANDO');
        localStorage.setItem(`agorax_onboarding_${schema_name}`, 'CONVERSANDO');
      } else {
        setEstadoOnboarding('ESPERANDO_UBICACION');
        localStorage.setItem(`agorax_onboarding_${schema_name}`, 'ESPERANDO_UBICACION');
      }
      setCargando(false);
      cargarAtencionInfo();
      return;
    }

    // Hay pedido activo: cargar la conversacion de ese pedido
    if (pedidoActivo.id) {
      // pedidoRef marca el pedido que se esta VIENDO en el chat (para adjuntar
      // mensajes y filtrar el hilo). pedidoKey marca el pedido EDITABLE; solo se
      // actualiza si el pedido seleccionado aun se puede editar, para no repuntar
      // el carrito/sync del pedido editable hacia un pedido de solo lectura.
      pedidoRef.current = pedidoActivo.id;
      const editable = pedidoActivo.estado === 'pendiente' ||
        (pedidoActivo.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision);
      if (editable) localStorage.setItem(pedidoKey, pedidoActivo.id);
      // Cargar el carrito de este pedido (misma clave que usa el catalogo).
      const savedCart = localStorage.getItem(cartKeyForChat(pedidoActivo.id));
      if (savedCart) {
        try {
          const parsed = JSON.parse(savedCart);
          setCarrito(parsed);
          carritoRef.current = parsed;
        } catch {}
      } else {
        setCarrito([]);
        carritoRef.current = [];
        // Rehidratar desde el servidor si no hay carrito local (pedido armado por
        // el bot/chat o en otro dispositivo).
        (async () => {
          try {
            const r = await axios.get(`${API_URL}/pedidos/${pedidoActivo.id}?schema_name=${schema_name}&session_id=${sessionId}`);
            if (r.data.status === 'success' && r.data.data?.productos) {
              let prods = r.data.data.productos;
              if (typeof prods === 'string') prods = JSON.parse(prods);
              if (Array.isArray(prods) && prods.length > 0) {
                const cart = prods.map((p) => ({
                  codigo: p.codigo || '',
                  nombre: p.nombre || '',
                  precio: parseFloat(p.precio || 0),
                  precio_bs: 0,
                  cantidad: p.cantidad || 1,
                  stock: null,
                  pesable: p.pesable || false,
                  imagen_url: p.imagen_url || '',
                  departamento: '',
                  etiquetas: p.etiquetas || [],
                  etiquetas_extra: p.etiquetas_extra || 0,
                  combo_id: p.combo_id || null,
                  componentes: p.componentes || undefined,
                }));
                setCarrito(cart);
                carritoRef.current = cart;
                localStorage.setItem(cartKeyForChat(pedidoActivo.id), JSON.stringify(cart));
              }
            }
          } catch {}
        })();
      }
    }
    setEstadoPedido(pedidoActivo.estado);
    if (pedidoActivo.codigo_sede) setSede(pedidoActivo.codigo_sede);

    try {
      const params = pedidoActivo.id ? { pedido_id: pedidoActivo.id } : {};
      const res = await axios.get(`${API_URL}/chat/historial/${schema_name}/${sessionId}`, { params });
      if (res.data.status === 'success') {
        const msgs = (res.data.data || []).map(normalizarMensaje);

        if (msgs.length === 0) {
          setEstadoOnboarding('CONVERSANDO');
          localStorage.setItem(`agorax_onboarding_${schema_name}`, 'CONVERSANDO');
          const welcomeMsg = normalizarMensaje({
            id: `welcome_${schema_name}_${sessionId}`,
            mensaje:
              'Veo que tienes un pedido en curso. Estoy aqui para ayudarte. Puedes agregar o modificar productos, hacer preguntas, o confirmar tu pedido cuando este listo. En que puedo ayudarte?',
            tipo: 'texto',
            hora: new Date().toISOString(),
            autor: 'bot',
            idcliente: null,
            leido: true,
          });
          setMensajes([welcomeMsg]);
          requestAnimationFrame(() => scrollToBottom(false));
          setCargando(false);
          cargarAtencionInfo();
          return;
        }

        setMensajes(msgs);
        await determinarEstadoOnboarding(msgs);
        requestAnimationFrame(() => scrollToBottom(false));
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setCargando(false);
    }

    cargarAtencionInfo();
  };

  const determinarEstadoOnboarding = async (msgs) => {
    if (msgs.length === 0) {
      const registrado = localStorage.getItem(`agorax_registro_${schema_name}`);
      if (registrado === 'true') {
        setEstadoOnboarding('ESPERANDO_UBICACION');
      } else {
        setEstadoOnboarding('NUEVO');
      }
      return;
    }
    try {
      const res = await axios.get(
        `${API_URL}/chat/cliente-por-session/${schema_name}/${sessionId}`
      );
      if (res.data.status === 'success' && res.data.data?.estado) {
        if (res.data.data.sede) setSede(res.data.data.sede);
        const estado = res.data.data.estado;
        if (ESTADOS_ONBOARDING.includes(estado)) {
          setEstadoOnboarding(estado);
        } else {
          setEstadoOnboarding(null);
        }
        return;
      }
    } catch {}
    setEstadoOnboarding(null);
  };

  const scrollToBottom = useCallback((smooth = true) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    });
    setMostrarScrollBtn(false);
  }, []);

  const handleChatScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setMostrarScrollBtn(dist > 500);
  }, []);

  const mostrarToast = (texto, tipo = 'cart') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, texto, tipo }]);
    const duracion = tipo === 'stock' ? 5000 : 3000;
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duracion);
  };

  const mostrarNotificacion = (data) => {
    playNotificationSound();
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification('Nuevo mensaje del asesor', {
          body: data.mensaje?.substring(0, 100) || 'Tienes un nuevo mensaje sobre tu pedido.',
          icon: '/logo-agorax.jpeg',
        });
      } catch { console.error('Error cargando pedidos en cargarHistorial'); }
    }
    mostrarToast('Nuevo mensaje del asesor', 'mensaje');
  };

  // ──────────────── carrito ────────────────

  const cargarAtencionInfo = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_URL}/chat/quien-atiende/${schema_name}/${sessionId}`);
      if (res.data.status === 'success') {
        setAtencionInfo({
          atendido_por: res.data.data.atendido_por || null,
          nombre_asesor: res.data.data.nombre_asesor || null,
        });
      }
    } catch {}
  };

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const recargarCarrito = async () => {
    try {
      const res = await axios.get(`${API_URL}/pedidos/mis-pedidos/${schema_name}/${sessionId}`);
      if (res.data.status === 'success') {
        const data = res.data.data || [];
        const pedidosActivos = data.filter(
          (p) => !['entregado','rechazado','cancelado','credito_pagado'].includes(p.estado)
        );

        if (pedidosActivos.length === 0) {
          setCarrito([]);
          localStorage.removeItem(carritoKeyBase);
          localStorage.removeItem(pedidoKey);
          setEstadoPedido(null);
          return;
        }

        // Recargar el carrito del pedido que se esta VIENDO (pedidoRef), no el
        // pedidoKey editable. Esto evita pisar el carrito del pedido visto con
        // los items de otro pedido cuando se alterna entre pestañas.
        let activo = null;
        if (pedidoRef.current) {
          activo = pedidosActivos.find((p) => p.id === parseInt(pedidoRef.current, 10));
        }
        if (!activo) {
          activo = pedidosActivos.find((p) => p.session_id === sessionId) || pedidosActivos[0];
        }

        if (activo) {
          if (activo.id) {
            pedidoRef.current = activo.id;
            const editable = activo.estado === 'pendiente' ||
              (activo.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision);
            if (editable) localStorage.setItem(pedidoKey, activo.id);
          }
          setEstadoPedido(activo.estado);
          if (activo.productos) {
            const prods = Array.isArray(activo.productos)
              ? activo.productos
              : JSON.parse(activo.productos);
            if (prods.length > 0) {
              const stockExistente = {};
              (carritoRef.current || []).forEach(c => {
                if (c.codigo && c.stock && c.stock !== 99 && c.stock !== 999) {
                  stockExistente[c.codigo] = c.stock;
                }
              });
              const cartItems = prods.map((p) => ({
                ...p,
                cantidad: p.cantidad || 1,
                stock: stockExistente[p.codigo] || 99,
                etiquetas: p.etiquetas || p.etiquetas_seleccionadas ? p.etiquetas || ((p.etiquetas_seleccionadas || []).map(id => ({id, nombre: `#${id}`}))) : [],
                etiquetas_extra: p.etiquetas_extra || 0,
              }));
              // Si es el eco de nuestro propio sync-cart (mismos productos), no
              // reaplicamos el carrito: evita el loop sync-cart -> pedido_actualizado
              // -> setCarrito -> sync-cart y su sonido repetido en pedidos 'pendiente'.
              if (firmaProductos(cartItems) === firmaProductos(carritoRef.current)) return;
              setCarrito(cartItems);
              localStorage.setItem(cartKeyForChat(activo.id), JSON.stringify(cartItems));
            }
          }
        }
      }
    } catch {}
  };

  const agregarAlCarrito = (prod, cant, fromBot = false) => {
    if (!fromBot && pedidoBloqueado) {
      mostrarToast('Tu pedido está en revisión. No se pueden hacer cambios.');
      return;
    }
    const stockReal = prod.stock && prod.stock > 0 ? prod.stock : null;
    if (stockReal === null && !fromBot && !pva && !prod.combo_id) {
      mostrarToast('Este producto est\u00e1 agotado');
      return;
    }
    const comboCodigo = prod.combo_id ? `combo_${prod.combo_id}` : null;
    const codigoItem = prod.codigo || comboCodigo || '';
    setCarrito((prev) => {
      const existe = prev.find((c) => c.codigo === codigoItem);
      const stockMax = prod.combo_id || pva
        ? Infinity
        : prod.pesable ? (stockReal || 1) * 1000 : (stockReal || 1);
      const nuevo = existe
        ? prev.map((c) =>
            c.codigo === codigoItem
              ? { ...c, cantidad: (prod.combo_id || pva) ? c.cantidad + cant : Math.min(c.cantidad + cant, stockMax) }
              : c
          )
        : [
            ...prev,
            {
              ...prod,
              codigo: codigoItem,
              cantidad: (prod.combo_id || pva) ? Math.max(1, cant) : Math.min(cant, stockMax),
              pesable: prod.pesable || false,
              combo_id: prod.combo_id || null,
            },
          ];
      if (!fromBot) {
        const cantMostrar = prod.pesable ? (cant / 1000).toFixed(2) + ' kg' : cant + ' und';
        setTimeout(() => {
          mostrarToast(`🛒 Agregaste ${prod.nombre} (${cantMostrar}) al pedido`);
          const updatedCart = nuevo;
          socket.emit('cart_update', {
            schema: schema_name,
            carrito: updatedCart,
            session_id: sessionId,
          });
        }, 0);
      }
      return nuevo;
    });
  };

  const actualizarCantidad = (codigo, delta) => {
    if (pedidoBloqueado) {
      mostrarToast('Tu pedido est\u00e1 en revisi\u00f3n. No se pueden hacer cambios.');
      return;
    }
    setCarrito((prev) => {
      const nuevo = prev.map((c) => {
        if (c.codigo !== codigo) return c;
            const stockMax = pva
              ? Infinity
              : c.pesable ? c.stock * 1000 : c.stock;
            const nueva = c.cantidad + delta;
            if (nueva < 0) return c;
            if (!pva && c.stock != null && stockMax >= 0 && nueva > stockMax) {
          setTimeout(() => mostrarToast(`Solo hay ${c.pesable ? (stockMax / 1000).toFixed(1) + ' kg' : stockMax + ' und'} disponibles`), 0);
          return c;
        }
        return { ...c, cantidad: nueva };
      });
      const item = nuevo.find((c) => c.codigo === codigo);
      if (item && delta !== 0) {
        const cantMostrar = item.pesable
          ? (item.cantidad / 1000).toFixed(2) + ' kg'
          : item.cantidad + ' und';
        setTimeout(() => {
          if (item.cantidad === 0) {
            mostrarToast(`🗑️ ${item.nombre} fue reducido a 0. Usa el carrito para eliminarlo.`);
          } else {
            mostrarToast(`✏️ ${item.nombre}: ${cantMostrar}`);
          }
          socket.emit('cart_update', {
            schema: schema_name,
            carrito: nuevo.filter((c) => c !== null),
            session_id: sessionId,
          });
        }, 0);
      }
      return nuevo;
    });
  };

  const eliminarDelCarrito = (codigo, fromBot = false) => {
    if (!fromBot && pedidoBloqueado) {
      mostrarToast('Tu pedido está en revisión. No se pueden hacer cambios.');
      return;
    }
    const prev = carritoRef.current;
    const item = prev.find((c) => c.codigo === codigo);
    const nuevo = prev.filter((c) => c.codigo !== codigo);
    carritoRef.current = nuevo;
    setCarrito(nuevo);
    if (!fromBot) {
      if (item) mostrarToast(`🗑️ Eliminaste ${item.nombre} del pedido`);
      socket.emit('cart_update', {
        schema: schema_name,
        carrito: nuevo,
        session_id: sessionId,
      });
      const pid = localStorage.getItem(pedidoKey);
      if (pid) syncCartToPedido(nuevo, parseInt(pid, 10));
    }
  };

  const vaciarCarrito = () => {
    if (pedidoBloqueado) return;
    carritoRef.current = [];
    setCarrito([]);
    mostrarToast('🗑️ Carrito vaciado');
    const pid = localStorage.getItem(pedidoKey);
    if (pid) syncCartToPedido([], parseInt(pid, 10));
  };

  const irAConfirmarEnDetalle = async () => {
    if (carrito.length === 0) {
      mostrarToast('Tu carrito está vacío');
      return;
    }
    const pid = localStorage.getItem(pedidoKey);
    if (!pid) {
      mostrarToast('Aun no tienes un pedido en curso');
      return;
    }
    try {
      await syncCartToPedido(carrito, parseInt(pid, 10));
    } catch {}
    setCarritoAbierto(false);
    if (onPedidoConfirmado) onPedidoConfirmado();
    if (onConfirmarPedido) onConfirmarPedido(parseInt(pid, 10));
    else navigate(`/pedido/${pid}`);
  };

  const usarUbicacionGPS = () => {
    if (!navigator.geolocation) {
      const sedeDefault = (config?.sedes || []).find((s) => s.activa !== false && s.lat && s.lng);
      setGpsCoords(sedeDefault ? { lat: sedeDefault.lat, lng: sedeDefault.lng } : { lat: 10.4806, lng: -66.9036 });
      setGpsError('Tu navegador no soporta geolocalización. Arrastra el pin a tu dirección.');
      setGpsModalOpen(true);
      return;
    }
    setGpsObteniendo(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsModalOpen(true);
        setGpsObteniendo(false);
      },
      () => {
        setGpsObteniendo(false);
        const sedeDefault = (config?.sedes || []).find((s) => s.activa !== false && s.lat && s.lng);
        setGpsCoords(sedeDefault ? { lat: sedeDefault.lat, lng: sedeDefault.lng } : { lat: 10.4806, lng: -66.9036 });
        setGpsError('No obtuvimos tu ubicación automáticamente. Arrastra el pin a tu dirección.');
        setGpsModalOpen(true);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  };

  const usarUbicacionGPSDirecto = () => {
    if (!navigator.geolocation) {
      setGpsError('Tu navegador no soporta geolocalización.');
      return;
    }
    setGpsObteniendo(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setGpsObteniendo(false);
        setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        confirmarUbicacionGPSDirecto(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        setGpsObteniendo(false);
        if (err.code === 1) {
          setGpsError('Permiso de ubicación denegado. Concede el permiso y vuelve a intentarlo.');
        } else {
          setGpsError('No se pudo obtener tu ubicación. Intenta de nuevo.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const confirmarUbicacionGPSDirecto = async (lat, lng) => {
    setEnviando(true);
    setBotEscribiendo(true);
    try {
      const res = await axios.post(`${API_URL}/chat/ubicacion/${schema_name}/${sessionId}`, {
        lat,
        lng,
      });
      if (res.data.status === 'success') {
        const tempId = `temp_${++tempIdCounter.current}`;
        const ahora = new Date().toISOString();
        setMensajes((prev) => [
          ...prev,
          normalizarMensaje({
            id: tempId,
            mensaje: 'Ubicación enviada por GPS',
            tipo: 'texto',
            hora: ahora,
            autor: 'cliente',
            idcliente: null,
          }),
          normalizarMensaje({
            id: res.data.data?.id || `temp_${++tempIdCounter.current}`,
            mensaje: res.data.respuesta,
            tipo: 'texto',
            hora: res.data.data?.hora || ahora,
            autor: 'bot',
            idcliente: res.data.data?.idcliente,
          }),
        ]);
        setEstadoOnboarding('CONVERSANDO');
        localStorage.setItem(`agorax_onboarding_${schema_name}`, 'CONVERSANDO');
        if (res.data.pedido_id) {
          localStorage.setItem(pedidoKey, res.data.pedido_id);
          setEstadoPedido('pendiente');
        }
        if (res.data.sede) setSede(res.data.sede);
        setGpsCoords(null);
        requestAnimationFrame(() => scrollToBottom());
      }
    } catch (err) {
      setGpsError('Error al guardar la ubicación. Intenta de nuevo.');
    } finally {
      setEnviando(false);
      setBotEscribiendo(false);
    }
  };

  const confirmarUbicacionGPS = async () => {
    if (!gpsCoords) return;
    setGpsModalOpen(false);
    setEnviando(true);
    setBotEscribiendo(true);
    try {
      const res = await axios.post(`${API_URL}/chat/ubicacion/${schema_name}/${sessionId}`, {
        lat: gpsCoords.lat,
        lng: gpsCoords.lng,
      });
      if (res.data.status === 'success') {
        const tempId = `temp_${++tempIdCounter.current}`;
        const ahora = new Date().toISOString();
        setMensajes((prev) => [
          ...prev,
          normalizarMensaje({
            id: tempId,
            mensaje: 'Ubicación enviada por GPS',
            tipo: 'texto',
            hora: ahora,
            autor: 'cliente',
            idcliente: null,
          }),
          normalizarMensaje({
            id: res.data.data?.id || `temp_${++tempIdCounter.current}`,
            mensaje: res.data.respuesta,
            tipo: 'texto',
            hora: res.data.data?.hora || ahora,
            autor: 'bot',
            idcliente: res.data.data?.idcliente,
          }),
        ]);
        setEstadoOnboarding('CONVERSANDO');
        localStorage.setItem(`agorax_onboarding_${schema_name}`, 'CONVERSANDO');
        if (res.data.pedido_id) {
          localStorage.setItem(pedidoKey, res.data.pedido_id);
          setEstadoPedido('pendiente');
        }
        if (res.data.sede) setSede(res.data.sede);
        setGpsCoords(null);
        requestAnimationFrame(() => scrollToBottom());
      }
    } catch (err) {
      console.error('Error guardando ubicacion:', err);
      setGpsError('Error al guardar la ubicación. Intenta de nuevo.');
    } finally {
      setEnviando(false);
      setBotEscribiendo(false);
    }
  };

  const cargarDireccionesChat = useCallback(async () => {
    if (!sessionId) return;
    setCargandoDirsChat(true);
    try {
      const res = await axios.get(`${API_URL}/chat/direcciones/${schema_name}/${sessionId}`);
      if (res.data.status === 'success') {
        const dirs = (res.data.data || []).filter((d) => d.lat && d.lng);
        setDireccionesChat(dirs);
      }
    } catch {
      setDireccionesChat([]);
    } finally {
      setCargandoDirsChat(false);
    }
  }, [sessionId, schema_name]);

  useEffect(() => {
    if (estadoOnboarding === 'ESPERANDO_UBICACION') cargarDireccionesChat();
  }, [estadoOnboarding, cargarDireccionesChat]);

  const usarDireccionGuardadaChat = useCallback(
    async (dir) => {
      setEnviando(true);
      setBotEscribiendo(true);
      try {
        const res = await axios.post(`${API_URL}/chat/ubicacion/${schema_name}/${sessionId}`, {
          lat: dir.lat,
          lng: dir.lng,
        });
        if (res.data.status === 'success') {
          const tempId = `temp_${Date.now()}`;
          const ahora = new Date().toISOString();
          setMensajes((prev) => [
            ...prev,
            normalizarMensaje({
              id: tempId,
              mensaje: `Usando dirección: ${dir.nombre || dir.direccion}`,
              tipo: 'texto',
              hora: ahora,
              autor: 'cliente',
              idcliente: null,
            }),
            normalizarMensaje({
              id: tempId + '_r',
              mensaje: res.data.respuesta,
              tipo: 'texto',
              hora: ahora,
              autor: 'bot',
              idcliente: null,
            }),
          ]);
          setEstadoOnboarding('CONVERSANDO');
          localStorage.setItem(`agorax_onboarding_${schema_name}`, 'CONVERSANDO');
          if (res.data.pedido_id) {
            localStorage.setItem(pedidoKey, res.data.pedido_id);
            setEstadoPedido('pendiente');
          }
          if (res.data.sede) setSede(res.data.sede);
          requestAnimationFrame(() => scrollToBottom());
        }
      } catch (err) {
        setGpsError('Error al usar la dirección guardada. Intenta de nuevo.');
      } finally {
        setEnviando(false);
        setBotEscribiendo(false);
      }
    },
    [schema_name, sessionId, pedidoKey, scrollToBottom]
  );

  const dirAutoSelectRef = useRef(false);
  useEffect(() => {
    if (
      estadoOnboarding === 'ESPERANDO_UBICACION' &&
      !cargandoDirsChat &&
      direccionesChat.length === 1 &&
      !dirAutoSelectRef.current
    ) {
      dirAutoSelectRef.current = true;
      usarDireccionGuardadaChat(direccionesChat[0]);
    }
  }, [estadoOnboarding, cargandoDirsChat, direccionesChat, usarDireccionGuardadaChat]);

  const totalCarrito = carrito.reduce((s, c) => {
    const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
    return s + base + etiquetaTotal(c);
  }, 0);
  const totalCarritoBs = carrito.reduce((s, c) => {
    const base = c.pesable ? (c.precio_bs * c.cantidad) / 1000 : c.precio_bs * c.cantidad;
    return s + base;
  }, 0);
  const pedidoBloqueado =
    estadoPedido &&
    estadoPedido !== 'pendiente' &&
    estadoPedido !== 'rechazado' &&
    estadoPedido !== 'cancelado' &&
    !(estadoPedido === 'en_revision' && config?.cliente_puede_modificar_en_revision);

  const esPedidoBloqueado = (est) =>
    est && est !== 'pendiente' && est !== 'rechazado' && est !== 'cancelado' &&
    !(est === 'en_revision' && config?.cliente_puede_modificar_en_revision);

  const enviarMensaje = async (e) => {
    e?.preventDefault();
    const msgTexto = texto.trim();
    if (!msgTexto || enviando || !sessionId || estadoOnboarding === 'ESPERANDO_UBICACION') return;

    const normConfirm = msgTexto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    if (
      (normConfirm === 'confirmar pedido' || normConfirm === 'confirmar') &&
      onConfirmarPedido &&
      carrito.length > 0 &&
      localStorage.getItem(pedidoKey)
    ) {
      setTexto('');
      irAConfirmarEnDetalle();
      return;
    }
    const tempId = `temp_${++tempIdCounter.current}`;
    const ahora = new Date().toISOString();
    const replyId = respuestaA ? respuestaA.id : null;
    const replyPreview = respuestaA
      ? {
          id: respuestaA.id,
          mensaje: respuestaA.mensaje,
          autor: respuestaA.autor,
          tipo: respuestaA.tipo,
          metadata: respuestaA.metadata,
        }
      : null;
    setTexto('');
    setEnviando(true);
    setRespuestaA(null);
    if (!pedidoBloqueado) setBotEscribiendo(true);

    const msgCliente = normalizarMensaje({
      id: tempId,
      mensaje: msgTexto,
      tipo: 'texto',
      hora: ahora,
      autor: 'cliente',
      idcliente: null,
      respuesta_a_id: replyId,
      respuesta_a: replyPreview,
    });
    setMensajes((prev) => [...prev, msgCliente]);
    scrollToBottom(false);
    requestAnimationFrame(() => scrollToBottom(false));

    try {
      const payload = {
        mensaje: msgTexto,
        session_id: sessionId,
        schema_name: schema_name,
        carrito: carrito,
      };
      if (pedidoRef.current) payload.pedido_id = pedidoRef.current;
      if (replyId) payload.respuesta_a_id = replyId;
      const res = await axios.post(`${API_URL}/chat/enviar`, payload);
      if (res.data.status === 'success') {
        const data = res.data;

        // Auto-agregar al carrito si el bot devuelve accion 'agregar'
        if (data.accion === 'agregar' || data.tipo === 'agregado') {
          if (data.items_agregados && data.items_agregados.length > 0) {
            data.items_agregados.forEach((prod) => {
              const qty = prod._cantidad || prod.cantidad || (prod.pesable ? 500 : 1);
              agregarAlCarrito(prod, qty, true);
              mostrarToast(`🛒 Agregaste ${prod.nombre} al pedido`);
            });
          } else if (data.producto) {
            agregarAlCarrito(data.producto, data.cantidad || 1, true);
            mostrarToast(`🛒 Agregaste ${data.producto?.nombre || 'producto'} al pedido`);
          }
        }

        // Eliminar del carrito si el bot devuelve accion 'quitar'
        if (data.accion === 'quitar' && data.producto) {
          eliminarDelCarrito(data.producto.nombre, true);
          mostrarToast(`🗑️ ${data.producto?.nombre || 'Producto'} eliminado del pedido`);
        }

        // Reemplazar en el carrito si el bot devuelve accion 'reemplazar'
        if (data.accion === 'reemplazar') {
          if (data.producto_anterior) {
            eliminarDelCarrito(
              data.producto_anterior.keyword || data.producto_anterior.nombre,
              true
            );
          }
          if (data.producto) {
            agregarAlCarrito(data.producto, data.cantidad || 1, true);
            mostrarToast(`🔄 ${data.producto?.nombre || 'Producto'} reemplazado en el pedido`);
          }
        }

        setMensajes((prev) => {
          const updated = prev.map((m) =>
            m.id === tempId
              ? normalizarMensaje({
                  id: tempId,
                  mensaje: msgTexto,
                  tipo: 'texto',
                  hora: ahora,
                  autor: 'cliente',
                  idcliente: data.data?.idcliente,
                })
              : m
          );
          const botId = data.data?.id;
          if (!data.respuesta) return updated;
          if (botId && updated.some((m) => m.id === botId)) return updated;
          return [
            ...updated,
            normalizarMensaje({
              id: botId || `temp_${++tempIdCounter.current}`,
              mensaje: data.respuesta,
              tipo: data.tipo || 'texto',
              accion: data.accion || data.tipo || 'texto',
              hora: data.data?.hora || ahora,
              autor: 'bot',
              idcliente: data.data?.idcliente,
              producto: data.producto || null,
              cantidad: data.cantidad,
              productos: data.productos || [],
              alternativas: data.alternativas,
              todas_opciones_keyword: data.todas_opciones_keyword || '',
              items_agregados: data.items_agregados || [],
            }),
          ];
        });
        setBotEscribiendo(false);
        if (data.estado) {
          if (data.estado === 'SEDE_VENCIDA') {
            setSedeVencida(true);
          } else {
            setEstadoOnboarding(data.estado);
            localStorage.setItem(`agorax_onboarding_${schema_name}`, data.estado);
          }
        }

        // Si el pedido fue confirmado, navegar al detalle
        if (data.tipo === 'pedido' || data.accion === 'confirmar_pedido') {
          const pidConfirm = localStorage.getItem(pedidoKey);
          if (onPedidoConfirmado) onPedidoConfirmado();
          if (pidConfirm) {
            if (onConfirmarPedido) onConfirmarPedido(parseInt(pidConfirm, 10));
            else navigate(`/pedido/${pidConfirm}`);
          }
        }

        requestAnimationFrame(() => scrollToBottom());
      }
    } catch (err) {
      console.error('Error:', err);
      setBotEscribiendo(false);
    } finally {
      setEnviando(false);
    }
  };

  const verOpciones = (keyword) => {
    setTexto(`ver opciones ${keyword}`);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const abrirDetalleProducto = async (codigo) => {
    setCargandoProductoDetalle(true);
    setProductoDetalle(null);
    try {
      const res = await axios.get(`${API_URL}/catalogo/producto/${codigo}`, {
        params: { sede, schema_name },
      });
      if (res.data?.status === 'success') {
        setProductoDetalle(res.data.data);
      }
    } catch (err) {
      console.error('Error cargando detalle de producto:', err);
    } finally {
      setCargandoProductoDetalle(false);
    }
  };

  const cerrarDetalleProducto = () => setProductoDetalle(null);

  const handleImagenSelect = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length > 0) {
      agregarImagenes(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (camaraInputRef.current) camaraInputRef.current.value = '';
  };

  const agregarImagenes = (files) => {
    setImagenesPreview((prev) => [...prev, ...files]);
    setImagenSeleccionadaIdx(0);
    setImagenModalOpen(true);
  };

  const quitarImagen = (idx) => {
    setImagenesPreview((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) {
        setImagenModalOpen(false);
      }
      return next;
    });
    setImagenSeleccionadaIdx((prev) => (prev >= idx && prev > 0 ? prev - 1 : prev));
  };

  const handlePasteImagenes = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      agregarImagenes(files);
    }
  };

  const cancelarImagen = () => {
    setImagenesPreview([]);
    setImagenModalOpen(false);
    setImagenSeleccionadaIdx(0);
  };

  const handleDocSelect = (e) => {
    const file = e.target.files[0];
    if (file) setDocPreview(file);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const cancelarDocumento = () => setDocPreview(null);

  const enviarDocumento = async () => {
    if (!docPreview || enviando || !sessionId) return;
    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append('file', docPreview);
      formData.append('session_id', sessionId);
      formData.append('schema_name', schema_name);
      const pid = localStorage.getItem(pedidoKey);
      if (pid) formData.append('pedido_id', pid);
      if (respuestaA) formData.append('respuesta_a_id', respuestaA.id);
      setRespuestaA(null);

      const res = await axios.post(`${API_URL}/chat/subir-documento`, formData);
      if (res.data.status === 'success') {
        setMensajes((prev) => [...prev, normalizarMensaje(res.data.data)]);
        requestAnimationFrame(() => scrollToBottom());
      } else {
        mostrarToast(res.data?.message || 'No se pudo enviar el documento', 'error');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo enviar el documento';
      mostrarToast(msg, 'error');
    } finally {
      setEnviando(false);
      setDocPreview(null);
    }
  };

  const enviarImagen = async () => {
    if (imagenesPreview.length === 0 || enviando || !sessionId) return;
    setEnviando(true);
    const cola = [...imagenesPreview];
    setImagenesPreview([]);
    setImagenModalOpen(false);
    setImagenSeleccionadaIdx(0);
    try {
      for (const imagen of cola) {
        const formData = new FormData();
        formData.append('file', imagen);
        formData.append('session_id', sessionId);
        formData.append('schema_name', schema_name);
        const pid = localStorage.getItem(pedidoKey);
        if (pid) formData.append('pedido_id', pid);
        if (respuestaA) formData.append('respuesta_a_id', respuestaA.id);
        const res = await axios.post(`${API_URL}/chat/subir-imagen`, formData);
        if (res.data.status === 'success') {
          setMensajes((prev) => [...prev, normalizarMensaje(res.data.data)]);
        }
      }
      setRespuestaA(null);
      requestAnimationFrame(() => scrollToBottom());
    } catch (err) {
      console.error('Error subiendo imagen:', err);
      mostrarToast('No se pudo enviar una imagen', 'error');
    } finally {
      setEnviando(false);
    }
  };

  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      transcripcionRef.current = '';

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const transcripcion = transcripcionRef.current.trim();
        await procesarAudio(blob, transcripcion);
      };

      mediaRecorder.start();

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-ES';
        recognition.interimResults = true;
        recognition.continuous = true;
        recognition.onresult = (e) => {
          let newFinal = '';
          for (let i = e.resultIndex; i < e.results.length; i++) {
            if (e.results[i].isFinal) {
              newFinal += ' ' + e.results[i][0].transcript;
            }
          }
          if (newFinal.trim()) {
            transcripcionRef.current = (transcripcionRef.current + ' ' + newFinal.trim()).trim();
          }
        };

        recognition.onerror = (e) => {
          if (e.error !== 'aborted') console.log('Speech error:', e.error);
        };
        recognition.start();
        speechRef.current = recognition;
      }

      setGrabando(true);
    } catch (err) {
      console.error('Error accediendo al microfono:', err);
      mostrarToast('No se pudo acceder al microfono', 'error');
    }
  };

  const detenerGrabacion = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (speechRef.current) {
      try {
        speechRef.current.stop();
      } catch { console.error('Error cargando pedidos en cargarHistorial'); }
      speechRef.current = null;
    }
    setGrabando(false);
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (speechRef.current) {
        try { speechRef.current.stop(); } catch {}
        speechRef.current = null;
      }
    };
  }, []);

  const procesarAudio = async (blob, transcripcion) => {
    if (!sessionId) return;
    setEnviando(true);
    try {
      const conAsesor = atencionInfo?.atendido_por;

      if (conAsesor) {
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');
        formData.append('session_id', sessionId);
        formData.append('schema_name', schema_name);
        formData.append('transcripcion', transcripcion);
        const pid = localStorage.getItem(pedidoKey);
        if (pid) formData.append('pedido_id', pid);
        if (respuestaA) formData.append('respuesta_a_id', respuestaA.id);
        setRespuestaA(null);

        const res = await axios.post(`${API_URL}/chat/subir-audio`, formData);
        if (res.data.status === 'success') {
          setMensajes((prev) => [...prev, normalizarMensaje(res.data.data)]);
          scrollToBottom(false);
          requestAnimationFrame(() => scrollToBottom(false));
        }
      } else {
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');
        formData.append('session_id', sessionId);
        formData.append('schema_name', schema_name);
        formData.append('transcripcion', transcripcion);
        const pid = localStorage.getItem(pedidoKey);
        if (pid) formData.append('pedido_id', pid);
        if (respuestaA) formData.append('respuesta_a_id', respuestaA.id);
        setRespuestaA(null);

        const audioRes = await axios.post(`${API_URL}/chat/subir-audio`, formData);
        if (audioRes.data.status === 'success') {
          setMensajes((prev) => [...prev, normalizarMensaje(audioRes.data.data)]);
          requestAnimationFrame(() => scrollToBottom());
        }

        const transcripcionFinal = audioRes.data?.data?.transcripcion || transcripcion;
        if (transcripcionFinal && transcripcionFinal.trim()) {
          setBotEscribiendo(true);
          const payload = {
            mensaje: transcripcionFinal.trim(),
            session_id: sessionId,
            schema_name: schema_name,
            carrito: carrito,
          };
          if (pid) payload.pedido_id = parseInt(pid, 10);
          const res = await axios.post(`${API_URL}/chat/enviar`, payload);
          if (res.data.status === 'success') {
            setBotEscribiendo(false);
            setMensajes((prev) => {
              const updated = [...prev];
              if (res.data.respuesta) {
                updated.push(
                  normalizarMensaje({
                    id: res.data.data?.id || `bot_${Date.now()}`,
                    mensaje: res.data.respuesta,
                    tipo: res.data.tipo || 'texto',
                    accion: res.data.accion || 'texto',
                    hora: res.data.data?.hora || new Date().toISOString(),
                    autor: 'bot',
                    idcliente: res.data.data?.idcliente,
                    producto: res.data.producto || null,
                    cantidad: res.data.cantidad,
                    productos: res.data.productos || [],
                    alternativas: res.data.alternativas,
                    items_agregados: res.data.items_agregados || [],
                  })
                );
              }
              return updated;
            });
            if (res.data.estado) {
              setEstadoOnboarding(res.data.estado);
              localStorage.setItem(`agorax_onboarding_${schema_name}`, res.data.estado);
            }
            const nuevoCarrito = res.data.carrito || res.data.data?.carrito;
            if (nuevoCarrito) {
              setCarrito(nuevoCarrito);
              localStorage.setItem(cartKeyForChat(pedidoRef.current), JSON.stringify(nuevoCarrito));
            }
            if (res.data.pedido_id) {
              localStorage.setItem(pedidoKey, res.data.pedido_id);
            }
            if (res.data.atencion_info) {
              setAtencionInfo(res.data.atencion_info);
              if (res.data.atencion_info.atendido_por) {
                setMensajes((prev) => [
                  ...prev,
                  {
                    id: 'sys_' + Date.now(),
                    mensaje: `Ahora te atiende ${res.data.atencion_info.nombre_asesor || 'un asesor'}`,
                    autor: 'sistema',
                    tipo: 'sistema',
                    hora: new Date().toISOString(),
                    metadata: {},
                  },
                ]);
                setEstadoOnboarding(null);
              }
            }
            requestAnimationFrame(() => scrollToBottom());
          }
        } else if (audioRes.data?.status === 'success') {
          setBotEscribiendo(true);
          const payload = {
            mensaje: '[Audio]',
            session_id: sessionId,
            schema_name: schema_name,
            carrito: carrito,
          };
          if (pid) payload.pedido_id = parseInt(pid, 10);
          await axios
            .post(`${API_URL}/chat/enviar`, payload)
            .catch((e) => console.error('Error enviando audio:', e?.message || e));
          setBotEscribiendo(false);
        }
      }
    } catch (err) {
      console.error('Error procesando audio:', err);
    } finally {
      setEnviando(false);
      setBotEscribiendo(false);
    }
  };

  const mostrarSeparadorFecha = (msg, idx) => {
    if (idx === 0) return true;
    try {
      return new Date(msg.hora).toDateString() !== new Date(mensajes[idx - 1].hora).toDateString();
    } catch {
      return false;
    }
  };

  const estaEnOnboarding = estadoOnboarding && ESTADOS_ONBOARDING.includes(estadoOnboarding);

  const placeholderOnboarding = () => {
    switch (estadoOnboarding) {
      case 'ESPERANDO_NOMBRE':
        return 'Escribe tu nombre completo...';
      case 'ESPERANDO_CEDULA':
        return 'Escribe tu numero de cedula...';
      case 'ESPERANDO_TELEFONO':
        return 'Escribe tu telefono...';
      default:
        return carrito.length > 0
          ? "Pregunta algo o escribe 'confirmar pedido'..."
          : 'Preguntame por productos, precios u horarios...';
    }
  };

  const pasosOnboarding = ['Nombre', 'Cedula', 'Telefono', 'Ubicacion'];
  const pasoActual = ESTADOS_ONBOARDING.indexOf(estadoOnboarding);

  const stockColor = (stock) =>
    stock > 20 ? 'var(--color-success)' : stock > 0 ? '#FF9500' : 'var(--color-danger)';
  const stockLabel = (stock) => (stock > 20 ? 'Disponible' : stock > 0 ? 'Pocas und' : 'Agotado');

  return (
    <div
      style={{
        ...S.wrapper,
      ...(compact
        ? { minHeight: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
        : { height: S.wrapper.height }),
        maxWidth: isDesktop ? '100%' : compact ? '100%' : S.wrapper.maxWidth,
      }}
    >
      {/* Toast notifications */}
      <div style={S.toastContainer}>
        {toasts.map((t) => (
          <div key={t.id} style={t.tipo === 'stock' ? S.toastStock : S.toastCart}>
            {t.tipo === 'stock' ? (
              <>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>{'\u26A0\uFE0F'}</span>
                <div style={{ flex: 1 }}>
                  <div style={S.toastTitle}>Stock bajo</div>
                  <div style={S.toastBody}>{t.texto}</div>
                </div>
              </>
            ) : (
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-primary)' }}>
                {t.texto}
              </span>
            )}
          </div>
        ))}
      </div>
      <SelectorPedidos
        pedidos={pedidosActivos}
        seleccionadoId={pedidoSeleccionadoId || pedidoRef.current}
        onSeleccionar={onSeleccionarPedido}
      />
      {!compact ? (
        <header style={S.header}>
          <div style={S.headerStart}>
            <button
              onClick={() => (onBack ? onBack() : navigate('/'))}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-surface)',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                marginRight: '4px',
              }}
            >
              <FiArrowLeft size={20} />
            </button>
            <img
              src={
                logoUrl
                  ? getImagenUrl(logoUrl)
                  : '/logo-agorax.jpeg'
              }
              alt={brandName}
              style={S.headerLogo}
            />
            <div>
              <div style={S.headerTitle}>{brandName}</div>
              <div style={S.headerSub}>
                <span
                  style={{
                    ...S.statusDot,
                    backgroundColor: conectado ? 'var(--color-success)' : 'var(--color-danger)',
                  }}
                />
                {conectado ? 'En linea' : 'Conectando...'}
                {atencionInfo?.atendido_por && atencionInfo?.nombre_asesor && (
                  <span
                    style={{
                      marginLeft: '8px',
                      padding: '0px 6px',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  >
                    {atencionInfo.nombre_asesor}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CampanaNotificaciones
              style={{ color: 'var(--color-surface)' }}
              onVerPedido={() =>
                onVerPedido ? onVerPedido() : navigate(`/pedido/${localStorage.getItem(pedidoKey)}`)
              }
            />
            {botEscribiendo && (
              <span style={S.typingBadge}>
                {atencionInfo?.atendido_por && atencionInfo?.nombre_asesor
                  ? `${atencionInfo.nombre_asesor} escribe...`
                  : 'escribiendo...'}
              </span>
            )}
            <button onClick={() => setCarritoAbierto(!carritoAbierto)} style={S.cartBtn}>
              <FiShoppingCart size={20} />
              {carrito.length > 0 && <span style={S.cartBadge}>{carrito.length}</span>}
            </button>
            {!atencionInfo?.atendido_por && (
              <button
                onClick={async () => {
                  if (solicitandoAsesor) return;
                  setSolicitandoAsesor(true);
                  try {
                    await axios.post(`${API_URL}/chat/solicitar-asesor/${schema_name}/${sessionId}`);
                    mostrarToast('Asesor solicitado. Te atenderan pronto.');
                  } catch {}
                  setSolicitandoAsesor(false);
                }}
                disabled={solicitandoAsesor}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '16px',
                  color: 'var(--color-surface)',
                  cursor: solicitandoAsesor ? 'default' : 'pointer',
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: solicitandoAsesor ? 0.6 : 1,
                }}
              >
                <FiUserPlus size={14} /> {solicitandoAsesor ? 'Solicitando...' : 'Solicitar asesor'}
              </button>
            )}
          </div>
        </header>
      ) : (
        <div
          style={{
            padding: '8px 24px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            flexShrink: 0,
            zIndex: 10,
            background: 'var(--color-bg)',
          }}
        >
          {botEscribiendo && (
            <span
              style={{
                fontSize: '11px',
                color: 'var(--color-text-secondary)',
                fontStyle: 'italic',
              }}
            >
              {atencionInfo?.atendido_por && atencionInfo?.nombre_asesor
                ? `${atencionInfo.nombre_asesor} escribe...`
                : 'escribiendo...'}
            </span>
          )}
          <button
            onClick={() => setCarritoAbierto(!carritoAbierto)}
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '20px',
              cursor: 'pointer',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <FiShoppingCart size={18} color="var(--color-secondary)" />
            {carrito.length > 0 && (
              <span
                style={{
                  backgroundColor: 'var(--color-danger)',
                  color: 'var(--color-surface)',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  padding: '1px 6px',
                  minWidth: '16px',
                  textAlign: 'center',
                }}
              >
                {carrito.reduce((s, c) => s + c.cantidad, 0)}
              </span>
            )}
          </button>
          {!atencionInfo?.atendido_por && (
            <button
              onClick={async () => {
                if (solicitandoAsesor) return;
                setSolicitandoAsesor(true);
                try {
                  await axios.post(`${API_URL}/chat/solicitar-asesor/${schema_name}/${sessionId}`);
                  mostrarToast('Asesor solicitado. Te atenderan pronto.');
                } catch {}
                setSolicitandoAsesor(false);
              }}
              disabled={solicitandoAsesor}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '20px',
                cursor: solicitandoAsesor ? 'default' : 'pointer',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-primary)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                opacity: solicitandoAsesor ? 0.6 : 1,
              }}
            >
              <FiUserPlus size={14} /> {solicitandoAsesor ? 'Solicitando...' : 'Asesor'}
            </button>
          )}
        </div>
      )}

      {estadoPedido && estadoPedido !== 'pendiente' && (
        <div
          onClick={() =>
            onVerPedido ? onVerPedido() : navigate(`/pedido/${localStorage.getItem(pedidoKey)}`)
          }
          style={{
            padding: '10px 16px',
            backgroundColor: 'var(--color-bg-hover)',
            borderBottom: '1px solid var(--color-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
          }}
        >
          <FiPackage size={18} color="var(--color-secondary)" />
          <div style={{ flex: 1, fontSize: '12px', color: 'var(--color-text-secondary)' }}>
            Tu pedido está en <strong>{estadoPedido}</strong>. Toca para ver el detalle.
          </div>
          <FiArrowRight size={16} color="var(--color-secondary)" />
        </div>
      )}

      {estaEnOnboarding && pasoActual >= 0 && !compact && (
        <div style={S.onboardingBar}>
          <div style={S.onboardingSteps}>
            {pasosOnboarding.map((paso, i) => (
              <div key={paso} style={S.onboardingStep}>
                <div
                  style={{
                    ...S.onboardingDot,
                    backgroundColor:
                      i < pasoActual
                        ? 'var(--color-success)'
                        : i === pasoActual
                          ? 'var(--color-secondary)'
                          : 'var(--color-border)',
                  }}
                >
                  {i < pasoActual ? '\u2713' : i + 1}
                </div>
                <span
                  style={{
                    ...S.onboardingLabel,
                    color:
                      i === pasoActual ? 'var(--color-secondary)' : 'var(--color-text-secondary)',
                    fontWeight: i === pasoActual ? 600 : 400,
                  }}
                >
                  {paso}
                </span>
                {i < pasosOnboarding.length - 1 && (
                  <div
                    style={{
                      ...S.onboardingLine,
                      backgroundColor:
                        i < pasoActual ? 'var(--color-success)' : 'var(--color-border)',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <main style={{ ...S.chatArea, flex: 1, minHeight: 0 }} ref={scrollRef} onScroll={handleChatScroll}>
        {estadoOnboarding === 'ESPERANDO_UBICACION' ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 24px',
              textAlign: 'center',
              minHeight: '100%',
            }}
          >
            <div
              style={{
                width: '88px',
                height: '88px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-bg-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
              }}
            >
              <FiMapPin size={48} color="var(--color-secondary)" />
            </div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-primary)',
                margin: '0 0 8px',
              }}
            >
              Donde entregamos tu pedido?
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.5,
                maxWidth: '280px',
                margin: '0 0 24px',
              }}
            >
              Necesitamos tu ubicación para registrar tu dirección de entrega y asignarte a la sede
              más cercana.
            </p>
            {gpsError && (
              <div
                style={{
                  fontSize: '13px',
                  color: 'var(--color-danger)',
                  backgroundColor: 'var(--color-danger-light)',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  maxWidth: '300px',
                }}
              >
                {gpsError}
              </div>
            )}
            {!cargandoDirsChat && direccionesChat.length > 0 && (
              <div style={{ width: '100%', maxWidth: '320px', marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '8px',
                  }}
                >
                  Usar direccion guardada:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {direccionesChat.map((dir) => (
                    <button
                      key={dir.id}
                      onClick={() => usarDireccionGuardadaChat(dir)}
                      disabled={enviando}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 14px',
                        borderRadius: '14px',
                        border: dir.principal
                          ? '2px solid #f0b400'
                          : '1px solid var(--color-border)',
                        backgroundColor: dir.principal ? '#fffdf5' : 'var(--color-surface)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        opacity: enviando ? 0.6 : 1,
                      }}
                    >
                      <FiHome
                        size={18}
                        color={dir.principal ? '#f0b400' : 'var(--color-secondary)'}
                        style={{ flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' }}
                        >
                          {dir.nombre || 'Sin nombre'}
                          {dir.principal && (
                            <span
                              style={{
                                fontSize: '10px',
                                color: '#f0b400',
                                marginLeft: '6px',
                                fontWeight: 500,
                              }}
                            >
                              FAVORITA
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-secondary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: '2px',
                          }}
                        >
                          {dir.direccion}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%',
                maxWidth: '300px',
              }}
            >
              {mostrarOpcionesGPS ? (
                <>
                  <button
                    onClick={usarUbicacionGPSDirecto}
                    disabled={gpsObteniendo}
                    style={{
                      padding: '14px 24px',
                      borderRadius: '14px',
                      border: 'none',
                      backgroundColor: 'var(--color-secondary)',
                      color: 'var(--color-surface)',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'opacity 0.2s',
                      fontFamily: 'inherit',
                    }}
                  >
                    <FiNavigation size={18} />
                    {gpsObteniendo ? 'Obteniendo ubicacion...' : 'Usar mi ubicacion actual'}
                  </button>
                  <button
                    onClick={usarUbicacionGPS}
                    style={{
                      padding: '14px 24px',
                      borderRadius: '14px',
                      border: '1.5px solid var(--color-secondary)',
                      backgroundColor: 'var(--color-surface)',
                      color: 'var(--color-secondary)',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <FiMapPin size={18} />
                    Elegir en el mapa
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setMostrarOpcionesGPS(true)}
                  style={{
                    padding: '14px 24px',
                    borderRadius: '14px',
                    border: '1.5px solid var(--color-border)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-secondary)',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <FiMapPin size={18} />
                  Nueva direccion
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={S.messagesContainer}>
            {pedidoBloqueado && (
              <div
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderLeft: '3px solid var(--color-secondary)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(41,82,204,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiPackage size={14} color="var(--color-secondary)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      marginBottom: '2px',
                    }}
                  >
                    Tu pedido está en revisión
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.4,
                    }}
                  >
                    Un asesor está revisando tu pedido. Puedes seguir chateando pero no se pueden
                    modificar los productos.
                  </div>
                </div>
              </div>
            )}
            {!pedidoBloqueado && estadoPedido === 'en_revision' && config?.cliente_puede_modificar_en_revision && (
              <div
                style={{
                  backgroundColor: '#f0fdf4',
                  borderLeft: '3px solid var(--color-success)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(52,168,83,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiPackage size={14} color="var(--color-success)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--color-success)',
                      marginBottom: '2px',
                    }}
                  >
                    Puedes modificar tu pedido
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.4,
                    }}
                  >
                    Agrega, elimina o modifica productos libremente. Cada cambio sera notificado al
                    asesor.
                  </div>
                </div>
              </div>
            )}
            {sedeVencida && (
              <div
                style={{
                  backgroundColor: '#FFEBEE',
                  border: '1px solid #EF5350',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  marginBottom: '12px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#C62828',
                    marginBottom: '4px',
                  }}
                >
                  Membresía vencida
                </div>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#B71C1C' }}>
                  Lo sentimos, la membresía de esta sede está vencida. Por favor contacta al
                  administrador para renovar el servicio.
                </span>
              </div>
            )}
            {cargando && (
              <div style={S.loadingContainer}>
                <div style={S.loadingDots}>
                  <span style={{ ...S.loadingDot, animationDelay: '0s' }} />
                  <span style={{ ...S.loadingDot, animationDelay: '0.2s' }} />
                  <span style={{ ...S.loadingDot, animationDelay: '0.4s' }} />
                </div>
                <span style={S.loadingText}>Cargando...</span>
              </div>
            )}
            {!cargando && mensajes.length === 0 && (
              <div style={S.emptyState}>
                <div style={S.emptyIcon}>{'\u{1F44B}'}</div>
                <div style={S.emptyTitle}>Bienvenido a {brandName}</div>
                <div style={S.emptyText}>
                  Soy tu asistente virtual. Para comenzar, necesito algunos datos para atenderte
                  mejor.
                </div>
              </div>
            )}
            {!conectado && mensajes.length > 0 && (
              <div style={{
                textAlign: 'center',
                padding: '8px 16px',
                margin: '4px 16px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 500,
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning)',
              }}>
                Reconectando...
              </div>
            )}
            {mensajes.map((msg, idx) => {
              const meta = msg.metadata;
              const esFase = msg.tipo === 'fase';
              const esCambios =
                msg.tipo === 'cambios' || (msg.autor === 'sistema' && meta && meta.cambios);
              const esImagen = msg.tipo === 'imagen';
              const esAudio = msg.tipo === 'audio';
              const esDocumento = msg.tipo === 'documento';
              const esTarjetaEspecial = esFase || esCambios;
              const esReplyable =
                ['cliente', 'asesor', 'agente', 'bot'].includes(msg.autor) &&
                ['texto', 'imagen', 'audio', 'documento'].includes(msg.tipo);

              const prevMsg = mensajes[idx - 1];
              const nextMsg = mensajes[idx + 1];
              const esMismoAutorCliente =
                (a, b) =>
                  !!a && !!b &&
                  a.autor === b.autor &&
                  a.autor !== 'sistema' &&
                  !['fase', 'cambios'].includes(a.tipo) &&
                  !['fase', 'cambios'].includes(b.tipo);
              const dentroDe5MinCliente = (a, b) =>
                !!a?.hora && !!b?.hora &&
                Math.abs(new Date(a.hora) - new Date(b.hora)) < 5 * 60 * 1000;
              const esContinuacion =
                esMismoAutorCliente(msg, prevMsg) && dentroDe5MinCliente(msg, prevMsg);
              const esContinuado =
                esMismoAutorCliente(msg, nextMsg) && dentroDe5MinCliente(msg, nextMsg);
              const esUltimoDeGrupo = !esContinuado;

              if (esTarjetaEspecial) {
                return (
                  <React.Fragment key={msg.id || idx}>
                    {mostrarSeparadorFecha(msg, idx) && (
                      <div style={S.dateSeparator}>
                        <span style={S.dateLabel}>{formatFecha(msg.hora)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}>
                      <div style={{ maxWidth: '95%', width: '100%' }}>
                        {esFase && (
                          <FasePedidoCard
                            fase={meta?.fase || msg.mensaje}
                            pedidoId={msg.pedido_id}
                          />
                        )}
                        {esCambios && (
                          <CambiosProductoCard
                            cambios={meta?.cambios || []}
                            mensaje={msg.mensaje}
                          />
                        )}
                        <div
                          style={{
                            fontSize: '10px',
                            color: 'var(--color-text-muted)',
                            textAlign: 'center',
                            marginTop: '2px',
                          }}
                        >
                          {formatHora(msg.hora)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={msg.id || idx}>
                  {mostrarSeparadorFecha(msg, idx) && (
                    <div style={S.dateSeparator}>
                      <span style={S.dateLabel}>{formatFecha(msg.hora)}</span>
                    </div>
                  )}
                  {msg.autor === 'bot' && (msg.tipo === 'productos' || msg.tipo === 'agregado') ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                      <div style={S.botAvatar}>{'\u{1F916}'}</div>
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          maxWidth: '90%',
                        }}
                      >
                        <div
                          style={{
                            ...S.bubble,
                            backgroundColor: 'var(--color-surface)',
                            padding: '10px 14px',
                            fontSize: '14px',
                            lineHeight: 1.5,
                          }}
                        >
                          <div style={S.bubbleText}>{msg.mensaje}</div>
                        </div>

                        {/* Producto individual (agregado automaticamente) */}
                        {msg.producto &&
                          (() => {
                            const p = msg.producto;
                            const stockColor =
                              p.stock > 20
                                ? '#0d904f'
                                : p.stock > 0
                                  ? '#ea8600'
                                  : 'var(--color-danger)';
                            const stockLabel =
                              p.stock > 20
                                ? 'Disponible'
                                : p.stock > 0
                                  ? 'Pocas unidades'
                                  : 'Agotado';
                            return (
                              <div
                                style={{ ...S.productCardHighlight, cursor: 'pointer' }}
                                onClick={() => p.codigo && abrirDetalleProducto(p.codigo)}
                              >
                                <ImagenProducto
                                  p={p}
                                  size={36}
                                  bg="var(--color-bg-hover)"
                                  icon={'\u{1F6D2}'}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={S.pName}>{p.nombre}</div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      flexWrap: 'wrap',
                                      marginTop: '3px',
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: 'var(--color-secondary)',
                                        fontSize: '14px',
                                      }}
                                    >
                                      ${fmtMonto(p.precio || 0)}
                                    </span>
                                    <span
                                      style={{
                                        color: 'var(--color-text-secondary)',
                                        fontSize: '12px',
                                      }}
                                    >
                                      Bs {fmtMonto(p.precio_bs || 0)}
                                    </span>
                                    {!pva && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        color: stockColor,
                                        fontWeight: 500,
                                      }}
                                    >
                                      ● {stockLabel}
                                    </span>
                                    )}
                                    {p.pesable && (
                                      <span style={S.pesableBadge}>{'\u2696\uFE0F'} Pesable</span>
                                    )}
                                  </div>
                                </div>
                                <span style={S.addedBadge}>{'\u2705'} Agregado</span>
                              </div>
                            );
                          })()}

                        {/* Items auto-agregados (multiples) */}
                        {msg.items_agregados && msg.items_agregados.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {msg.items_agregados.map((p, pi) => {
                              const stockColor =
                                p.stock > 20
                                  ? '#0d904f'
                                  : p.stock > 0
                                    ? '#ea8600'
                                    : 'var(--color-danger)';
                              const stockLabel =
                                p.stock > 20
                                  ? 'Disponible'
                                  : p.stock > 0
                                    ? 'Pocas unid'
                                    : 'Agotado';
                              return (
                                <div
                                  key={pi}
                                  style={{ ...S.productCard, cursor: 'pointer' }}
                                  onClick={() => p.codigo && abrirDetalleProducto(p.codigo)}
                                >
                                  <ImagenProducto p={p} size={32} bg="#e8f5e9" icon={'\u{1F6D2}'} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={S.pName}>{p.nombre}</div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        flexWrap: 'wrap',
                                        marginTop: '2px',
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 700,
                                          color: 'var(--color-secondary)',
                                          fontSize: '13px',
                                        }}
                                      >
                                        ${fmtMonto(p.precio || 0)}
                                      </span>
                                      <span
                                        style={{
                                          color: 'var(--color-text-secondary)',
                                          fontSize: '11px',
                                        }}
                                      >
                                        Bs {fmtMonto(p.precio_bs || 0)}
                                      </span>
                                      {!pva && (
                                      <span
                                        style={{
                                          fontSize: '10px',
                                          color: stockColor,
                                          fontWeight: 500,
                                        }}
                                      >
                                        ● {stockLabel}
                                      </span>
                                      )}
                                    </div>
                                  </div>
                                  <span style={S.addedBadge}>{'\u2705'} Agregado</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Lista de productos (tarjetas con boton) */}
                        {msg.productos && msg.productos.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {msg.productos.map((prod, pi) => {
                              const stockColor =
                                prod.stock > 20
                                  ? '#0d904f'
                                  : prod.stock > 0
                                    ? '#ea8600'
                                    : 'var(--color-danger)';
                              const stockLabel =
                                prod.stock > 20
                                  ? 'Disponible'
                                  : prod.stock > 0
                                    ? 'Pocas unid'
                                    : 'Agotado';
                              const yaEnCarrito = carrito.some((c) => c.nombre === prod.nombre);
                              return (
                                <div
                                  key={pi}
                                  style={{ ...S.productCard, cursor: 'pointer' }}
                                  onClick={() => prod.codigo && abrirDetalleProducto(prod.codigo)}
                                >
                                  <ImagenProducto
                                    p={prod}
                                    size={32}
                                    bg="var(--color-bg-hover)"
                                    icon={'\u{1F4E6}'}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={S.pName}>{prod.nombre}</div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        flexWrap: 'wrap',
                                        marginTop: '3px',
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 700,
                                          color: 'var(--color-secondary)',
                                          fontSize: '14px',
                                        }}
                                      >
                                        ${fmtMonto(prod.precio || 0)}
                                      </span>
                                      <span
                                        style={{
                                          color: 'var(--color-text-secondary)',
                                          fontSize: '12px',
                                        }}
                                      >
                                        Bs {fmtMonto(prod.precio_bs || 0)}
                                      </span>
                                      {!pva && (
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          color: stockColor,
                                          fontWeight: 500,
                                        }}
                                      >
                                        ● {stockLabel}
                                      </span>
                                      )}
                                      {prod.pesable && (
                                        <span style={S.pesableBadge}>{'\u2696\uFE0F'} Pesable</span>
                                      )}
                                    </div>
                                  </div>
                                  {yaEnCarrito ? (
                                    <span style={S.addedBadge}>{'\u2705'} En carrito</span>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        agregarAlCarrito(prod, prod.pesable ? 500 : 1);
                                      }}
                                      disabled={(!pva && (!prod.stock || prod.stock <= 0)) || pedidoBloqueado}
                                      style={{
                                        ...S.addBtn2,
                                        opacity: pva ? (!pedidoBloqueado ? 1 : 0.4) : (prod.stock && prod.stock > 0 && !pedidoBloqueado ? 1 : 0.4),
                                        cursor:
                                          pva ? (!pedidoBloqueado ? 'pointer' : 'default')
                                            : (prod.stock && prod.stock > 0 && !pedidoBloqueado
                                              ? 'pointer'
                                              : 'default'),
                                      }}
                                    >
                                      {pedidoBloqueado ? 'Bloqueado' : !pva && (!prod.stock || prod.stock <= 0) ? 'Agotado' : '\u{1F6D2} Agregar'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {msg.alternativas > 0 && msg.todas_opciones_keyword && (
                          <button
                            onClick={() => verOpciones(msg.todas_opciones_keyword)}
                            style={S.verOpcionesBtn}
                          >
                            {'\u{1F50D}'} Ver todas las opciones ({msg.alternativas})
                          </button>
                        )}
                        <div
                          style={{
                            fontSize: '10px',
                            color: 'var(--color-text-muted)',
                            textAlign: 'right',
                            paddingRight: '8px',
                          }}
                        >
                          {formatHora(msg.hora)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        ...S.bubbleRow,
                        justifyContent: msg.autor === 'cliente' ? 'flex-end' : 'flex-start',
                        marginTop: esContinuacion ? 2 : 6,
                      }}
                    >
                      {msg.autor !== 'cliente' && msg.autor !== 'sistema' && !esContinuacion && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                          <div style={{
                            ...S.botAvatar,
                            backgroundColor: msg.autor === 'asesor'
                              ? 'var(--color-success)'
                              : msg.autor === 'bot'
                                ? 'var(--color-bg-hover)'
                                : 'var(--color-bg-hover)',
                          }}>
                            {msg.autor === 'asesor' ? '\u{1F464}' : '\u{1F916}'}
                          </div>
                          {msg.autor === 'asesor' && (
                            <span style={{ fontSize: '9px', color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: '48px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              Asesor
                            </span>
                          )}
                        </div>
                      )}
                      {msg.autor !== 'cliente' && msg.autor !== 'sistema' && esContinuacion && (
                        <div style={{ width: '30px', flexShrink: 0 }} />
                      )}
                      {msg.autor === 'sistema' ? (
                        <div style={S.sistemaBubble}>{msg.mensaje}</div>
                      ) : (
                        <div
                          style={{
                            ...S.bubble,
                            ...estiloBurbuja(msg, esContinuado),
                            padding: esImagen ? '4px' : '8px',
                            maxWidth: esImagen ? '260px' : undefined,
                          }}
                        >
                          {esUltimoDeGrupo && (
                            <BurbujaCola
                              derecha={msg.autor === 'cliente'}
                              color={
                                msg.autor === 'cliente'
                                  ? 'var(--color-secondary)'
                                  : 'var(--color-surface)'
                              }
                            />
                          )}
                          <RespuestaPreview respuesta={msg.respuesta_a} />
                          {esImagen ? (
                            <ImagenMessageCard url={meta?.url || ''} />
                          ) : esAudio ? (
                            <AudioMessageCard
                              url={meta?.url || ''}
                              transcripcion={meta?.transcripcion || ''}
                              duracion={meta?.duracion || 0}
                            />
                          ) : esDocumento ? (
                            <DocumentoMessageCard
                              url={meta?.url || ''}
                              filename={meta?.filename || ''}
                              nombre={meta?.nombre_original || msg.mensaje}
                            />
                          ) : (
                            <div style={S.bubbleText}>{msg.mensaje}</div>
                          )}
                          {esUltimoDeGrupo && (
                            <div
                              style={{
                                ...S.bubbleTime,
                                color:
                                  msg.autor === 'cliente'
                                    ? 'rgba(255,255,255,0.75)'
                                    : 'var(--color-text-muted)',
                                textAlign: 'right',
                                ...(esImagen ? { paddingRight: '4px' } : {}),
                              }}
                            >
                              {formatHora(msg.hora)}
                            </div>
                          )}
                        </div>
                      )}
                      {esReplyable && (
                        <button
                          type="button"
                          onClick={() => setRespuestaA(msg)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            padding: '4px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            alignSelf: 'center',
                            flexShrink: 0,
                            opacity: 0.7,
                          }}
                          aria-label="Responder mensaje"
                        >
                          <FiCornerUpLeft size={15} />
                        </button>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {botEscribiendo && (
              <div style={{ ...S.bubbleRow, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                <div style={{
                  ...S.botAvatar,
                  backgroundColor: atencionInfo?.atendido_por
                    ? 'var(--color-success)'
                    : 'var(--color-bg-hover)',
                }}>
                  {atencionInfo?.atendido_por ? '\u{1F464}' : '\u{1F916}'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {atencionInfo?.atendido_por && atencionInfo?.nombre_asesor && (
                    <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', paddingLeft: '8px' }}>
                      {atencionInfo.nombre_asesor}
                    </span>
                  )}
                  <div
                    style={{
                      ...S.bubble,
                      backgroundColor: 'var(--color-surface)',
                      padding: '12px 16px',
                    }}
                  >
                    <div style={S.typingAnim}>
                      <span style={S.typingDot} />
                      <span style={{ ...S.typingDot, animationDelay: '0.2s' }} />
                      <span style={{ ...S.typingDot, animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {mostrarScrollBtn && (
        <button
          onClick={() => scrollToBottom(true)}
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '16px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-secondary)',
            fontSize: '18px',
            animation: 'slideUp 0.2s ease',
          }}
        >
          ↓
        </button>
      )}

      {/* Shopping Cart Panel */}
      {carritoAbierto && isMobile && (
        <>
          <div onClick={() => setCarritoAbierto(false)} style={S.cartMobileOverlay} />
          <div style={S.cartMobilePanel}>
            <div style={S.cartHeader}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Tu pedido</h3>
              <button onClick={() => setCarritoAbierto(false)} style={S.closeBtn}>
                <FiX size={18} />
              </button>
            </div>
            <div style={S.cartItems}>
              {carrito.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Tu carrito está vacío
                </div>
              ) : (
                carrito.map((c, i) => (
                  <div key={i} style={S.cartItem}>
                    {getImagenUrl(c.imagen_url) ? (
                      <img
                        src={getImagenUrl(c.imagen_url)}
                        alt={c.nombre}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    ) : (
                      <FiPackage
                        size={18}
                        color="var(--color-secondary)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.nombre}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {c.pesable
                          ? `${fmtMonto((c.precio * c.cantidad) / 1000)} / Bs ${fmtMonto((c.precio_bs * c.cantidad) / 1000)}`
                          : `${fmtMonto(c.precio * c.cantidad)} / Bs ${fmtMonto(c.precio_bs * c.cantidad)}`}
                      </div>
{!pva && (
                      <div style={{ fontSize: '11px', color: stockColor(c.stock) }}>
                        {stockLabel(c.stock)} Stock: {c.stock}
                        {c.pesable ? ' kg' : ''}
                      </div>
)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => actualizarCantidad(c.codigo, c.pesable ? -50 : -1)}
                        disabled={pedidoBloqueado || c.cantidad <= (c.pesable ? 50 : 0)}
                        style={{
                          ...S.cantSmall,
                          opacity: pedidoBloqueado || c.cantidad <= (c.pesable ? 50 : 0) ? 0.3 : 1,
                        }}
                      >
                        <FiMinus size={12} />
                      </button>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          minWidth: '55px',
                          textAlign: 'center',
                          opacity: c.cantidad === 0 ? 0.4 : 1,
                        }}
                      >
                        {c.pesable ? (c.cantidad / 1000).toFixed(2) + ' kg' : c.cantidad}
                      </span>
                      <button
                        onClick={() => actualizarCantidad(c.codigo, c.pesable ? 50 : 1)}
                        disabled={
                          !pva && (pedidoBloqueado || c.cantidad >= (c.pesable ? c.stock * 1000 : c.stock))
                        }
                        style={{
                          ...S.cantSmall,
                          opacity:
                            !pva && (pedidoBloqueado || c.cantidad >= (c.pesable ? c.stock * 1000 : c.stock))
                              ? 0.3
                              : 1,
                        }}
                      >
                        <FiPlus size={12} />
                      </button>
                      <button
                        onClick={() => eliminarDelCarrito(c.codigo)}
                        disabled={pedidoBloqueado}
                        style={{
                          ...S.cantSmall,
                          color: pedidoBloqueado
                            ? 'var(--color-text-muted)'
                            : 'var(--color-danger)',
                          borderColor: pedidoBloqueado ? 'var(--color-border)' : undefined,
                        }}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {carrito.length > 0 && (
              <div style={S.cartFooter}>
                <div style={S.cartTotal}>
                  <span>Total:</span>
                  <span style={{ fontWeight: 700 }}>
                    ${fmtMonto(totalCarrito)} / Bs {fmtMonto(totalCarritoBs)}
                  </span>
                </div>
                {!pedidoBloqueado && (
                  <>
                    <button onClick={vaciarCarrito} style={S.vaciarBtn}>
                      Vaciar
                    </button>
                    <button
                      onClick={() => {
                        if (carrito.length > 0) irAConfirmarEnDetalle();
                      }}
                      style={S.confirmarBtn}
                    >
                      Confirmar pedido
                    </button>
                  </>
                )}
                {pedidoBloqueado && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#FF9500',
                      textAlign: 'center',
                      fontWeight: 600,
                    }}
                  >
                    Pedido en revisión
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {carritoAbierto && !isMobile && (
        <>
          <div
            style={{
              ...S.cartPanel,
              transform: carritoAbierto ? 'translateX(0)' : 'translateX(100%)',
            }}
          >
            <div style={S.cartHeader}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Tu pedido</h3>
              <button onClick={() => setCarritoAbierto(false)} style={S.closeBtn}>
                <FiX size={18} />
              </button>
            </div>
            <div style={S.cartItems}>
              {carrito.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Tu carrito está vacío
                </div>
              ) : (
                carrito.map((c, i) => (
                  <div key={i} style={S.cartItem}>
                    {getImagenUrl(c.imagen_url) ? (
                      <img
                        src={getImagenUrl(c.imagen_url)}
                        alt={c.nombre}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    ) : (
                      <FiPackage
                        size={18}
                        color="var(--color-secondary)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.nombre}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {c.pesable
                          ? `${fmtMonto((c.precio * c.cantidad) / 1000)} / Bs ${fmtMonto((c.precio_bs * c.cantidad) / 1000)}`
                          : `${fmtMonto(c.precio * c.cantidad)} / Bs ${fmtMonto(c.precio_bs * c.cantidad)}`}
                      </div>
{!pva && (
                      <div style={{ fontSize: '11px', color: stockColor(c.stock) }}>
                        {stockLabel(c.stock)} Stock: {c.stock}
                        {c.pesable ? ' kg' : ''}
                      </div>
)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => actualizarCantidad(c.codigo, c.pesable ? -50 : -1)}
                        disabled={pedidoBloqueado || c.cantidad <= (c.pesable ? 50 : 0)}
                        style={{
                          ...S.cantSmall,
                          opacity: pedidoBloqueado || c.cantidad <= (c.pesable ? 50 : 0) ? 0.3 : 1,
                        }}
                      >
                        <FiMinus size={12} />
                      </button>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          minWidth: '55px',
                          textAlign: 'center',
                          opacity: c.cantidad === 0 ? 0.4 : 1,
                        }}
                      >
                        {c.pesable ? (c.cantidad / 1000).toFixed(2) + ' kg' : c.cantidad}
                      </span>
                      <button
                        onClick={() => actualizarCantidad(c.codigo, c.pesable ? 50 : 1)}
                        disabled={
                          !pva && (pedidoBloqueado || c.cantidad >= (c.pesable ? c.stock * 1000 : c.stock))
                        }
                        style={{
                          ...S.cantSmall,
                          opacity:
                            !pva && (pedidoBloqueado || c.cantidad >= (c.pesable ? c.stock * 1000 : c.stock))
                              ? 0.3
                              : 1,
                        }}
                      >
                        <FiPlus size={12} />
                      </button>
                      <button
                        onClick={() => eliminarDelCarrito(c.codigo)}
                        disabled={pedidoBloqueado}
                        style={{
                          ...S.cantSmall,
                          color: pedidoBloqueado
                            ? 'var(--color-text-muted)'
                            : 'var(--color-danger)',
                          borderColor: pedidoBloqueado ? 'var(--color-border)' : undefined,
                        }}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {carrito.length > 0 && (
              <div style={S.cartFooter}>
                <div style={S.cartTotal}>
                  <span>Total:</span>
                  <span style={{ fontWeight: 700 }}>
                    ${fmtMonto(totalCarrito)} / Bs {fmtMonto(totalCarritoBs)}
                  </span>
                </div>
                {!pedidoBloqueado && (
                  <>
                    <button onClick={vaciarCarrito} style={S.vaciarBtn}>
                      Vaciar
                    </button>
                    <button
                      onClick={() => {
                        if (carrito.length > 0) irAConfirmarEnDetalle();
                      }}
                      style={S.confirmarBtn}
                    >
                      Confirmar pedido
                    </button>
                  </>
                )}
                {pedidoBloqueado && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#FF9500',
                      textAlign: 'center',
                      fontWeight: 600,
                    }}
                  >
                    Pedido en revisión
                  </div>
                )}
              </div>
            )}
          </div>
          <div onClick={() => setCarritoAbierto(false)} style={S.overlay} />
        </>
      )}

      {/* GPS Location Modal */}
      {gpsModalOpen && gpsCoords && (
        <div style={S.gpsOverlay}>
          <div style={S.gpsModal}>
            <h3 style={S.gpsModalTitle}>Confirma tu ubicación</h3>
            <MapaUbicacion
              lat={gpsCoords.lat}
              lng={gpsCoords.lng}
              readOnly={false}
              onLocationChange={(lat, lng) => setGpsCoords({ lat, lng })}
              height="300px"
            />
            <p style={S.gpsHint}>Puedes arrastrar el pin si la ubicación no es exacta</p>
            <div style={S.gpsModalActions}>
              <button
                onClick={() => {
                  setGpsModalOpen(false);
                  setGpsCoords(null);
                }}
                style={S.gpsCancelBtn}
              >
                Cancelar
              </button>
              <button onClick={confirmarUbicacionGPS} style={S.gpsConfirmBtn}>
                Confirmar ubicación
              </button>
            </div>
          </div>
        </div>
      )}

      {imagenModalOpen && imagenesPreview.length > 0 && (
        <div
          onClick={cancelarImagen}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1E293B',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '520px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', color: '#F1F5F9', fontWeight: 600 }}>
                Vista previa {imagenesPreview.length > 1 ? `(${imagenesPreview.length})` : ''}
              </h3>
              <button
                onClick={cancelarImagen}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  padding: '4px',
                  fontSize: '20px',
                }}
              >
                {'\u2715'}
              </button>
            </div>

            <div
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '12px',
                backgroundColor: '#0F172A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={URL.createObjectURL(imagenesPreview[imagenSeleccionadaIdx])}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '360px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>

            {imagenesPreview.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  marginBottom: '12px',
                }}
              >
                {imagenesPreview.map((img, i) => (
                  <div
                    key={i}
                    style={{ position: 'relative', flexShrink: 0 }}
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Miniatura ${i + 1}`}
                      onClick={() => setImagenSeleccionadaIdx(i)}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border:
                          i === imagenSeleccionadaIdx
                            ? '2px solid var(--color-secondary)'
                            : '2px solid transparent',
                      }}
                    />
                    <button
                      onClick={() => quitarImagen(i)}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '11px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label="Quitar imagen"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                fontSize: '12px',
                color: '#94A3B8',
                marginBottom: '16px',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {imagenesPreview[imagenSeleccionadaIdx].name}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={cancelarImagen}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #334155',
                  backgroundColor: 'transparent',
                  color: '#94A3B8',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={enviarImagen}
                disabled={enviando}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--color-surface)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <FiSend size={14} /> Enviar{imagenesPreview.length > 1 ? ` (${imagenesPreview.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {productoDetalle && (
        <div
          onClick={cerrarDetalleProducto}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '20px 20px 0 0',
              padding: '20px 20px 28px',
              width: '100%',
              maxWidth: '480px',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 -4px 30px rgba(0,0,0,0.15)',
            }}
          >
            <button
              onClick={cerrarDetalleProducto}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'var(--color-bg)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                zIndex: 2,
              }}
            >
              <FiX size={22} />
            </button>

            {cargandoProductoDetalle ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '3px solid var(--color-border)',
                    borderTop: '3px solid var(--color-secondary)',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto',
                  }}
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '14px',
                    backgroundColor: 'var(--color-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                    overflow: 'hidden',
                  }}
                >
                  {getImagenUrl(productoDetalle.imagen_url) ? (
                    <img
                      src={getImagenUrl(productoDetalle.thumb_url || productoDetalle.imagen_url)}
                      alt={productoDetalle.descripcion}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <FiPackage size={64} color="var(--color-secondary)" />
                  )}
                </div>

                <h2
                  style={{
                    margin: '0 0 6px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    lineHeight: 1.3,
                    paddingRight: '40px',
                  }}
                >
                  {productoDetalle.descripcion}
                </h2>

                {productoDetalle.descripcion_completa &&
                  productoDetalle.descripcion_completa !== productoDetalle.descripcion && (
                    <p
                      style={{
                        fontSize: '13px',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.5,
                        margin: '0 0 8px',
                      }}
                    >
                      {productoDetalle.descripcion_completa}
                    </p>
                  )}
                {productoDetalle.descripcion_corta && (
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.5,
                      margin: '0 0 8px',
                    }}
                  >
                    {productoDetalle.descripcion_corta}
                  </p>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                    marginBottom: '12px',
                  }}
                >
                  <span
                    style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-secondary)' }}
                  >
                    ${fmtMonto(productoDetalle.precio || 0)}
                  </span>
                </div>

                <div
                  style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}
                >
                  {productoDetalle.pesable && (
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: '8px',
                        backgroundColor: '#fef7e0',
                        color: '#b06000',
                      }}
                    >
                      Vendido por peso
                    </span>
                  )}
                  {!pva && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '3px 10px',
                      borderRadius: '8px',
                      backgroundColor:
                        productoDetalle.stock > 20
                          ? '#e8f5e9'
                          : productoDetalle.stock > 0
                            ? '#fff3e0'
                            : 'var(--color-danger-light)',
                      color:
                        productoDetalle.stock > 20
                          ? '#0d904f'
                          : productoDetalle.stock > 0
                            ? '#ea8600'
                            : 'var(--color-danger)',
                    }}
                  >
                    {productoDetalle.stock > 20
                      ? `${productoDetalle.stock} en stock`
                      : productoDetalle.stock > 0
                        ? `Solo ${productoDetalle.stock} disponibles`
                        : 'Agotado'}
                  </span>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: 'var(--color-bg)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {productoDetalle.marca && (
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}
                    >
                      <span style={{ color: 'var(--color-text-secondary)' }}>Marca</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {productoDetalle.marca}
                      </span>
                    </div>
                  )}
                  {productoDetalle.departamento && (
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}
                    >
                      <span style={{ color: 'var(--color-text-secondary)' }}>Departamento</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {productoDetalle.departamento}
                      </span>
                    </div>
                  )}
                  {productoDetalle.grupo && (
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}
                    >
                      <span style={{ color: 'var(--color-text-secondary)' }}>Grupo</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {productoDetalle.grupo}
                      </span>
                    </div>
                  )}
                  {productoDetalle.subgrupo && (
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}
                    >
                      <span style={{ color: 'var(--color-text-secondary)' }}>Subgrupo</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {productoDetalle.subgrupo}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={cerrarDetalleProducto}
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {respuestaA && (
        <div
          style={{
            padding: '6px 16px',
            backgroundColor: 'var(--color-bg-hover)',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, borderLeft: '3px solid var(--color-secondary)', paddingLeft: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-secondary)' }}>
              Respondiendo a{' '}
              {respuestaA.autor === 'cliente'
                ? 'ti'
                : respuestaA.autor === 'bot'
                  ? 'asistente'
                  : 'asesor'}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {respuestaA.tipo === 'imagen'
                ? '📷 Imagen'
                : respuestaA.tipo === 'audio'
                  ? '🎤 Audio'
                  : respuestaA.mensaje}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRespuestaA(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              fontSize: '18px',
              lineHeight: 1,
              padding: '0 2px',
            }}
          >
            ×
          </button>
        </div>
      )}

      {docPreview && (
        <div
          style={{
            padding: '6px 16px',
            backgroundColor: 'var(--color-bg-hover)',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <FiFileText size={18} color="#ea4335" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              Documento listo para enviar
            </div>
            <div
              style={{
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {docPreview.name}
            </div>
          </div>
          <button
            type="button"
            onClick={cancelarDocumento}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              fontSize: '18px',
              lineHeight: 1,
              padding: '0 2px',
            }}
          >
            ×
          </button>
          <button
            type="button"
            onClick={enviarDocumento}
            disabled={enviando}
            style={{
              padding: '6px 12px',
              borderRadius: '16px',
              border: 'none',
              backgroundColor: 'var(--color-secondary)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: enviando ? 'default' : 'pointer',
              opacity: enviando ? 0.6 : 1,
            }}
          >
            Enviar
          </button>
        </div>
      )}

      {estadoOnboarding !== 'ESPERANDO_UBICACION' && (
        <div style={{ ...S.inputArea, position: 'relative', ...(compact ? { position: 'sticky', bottom: 0 } : {}) }}>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleImagenSelect}
          />
          <input
            type="file"
            ref={camaraInputRef}
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleImagenSelect}
          />
          <input
            type="file"
            ref={docInputRef}
            accept="application/pdf,.pdf"
            style={{ display: 'none' }}
            onChange={handleDocSelect}
          />
          <button
            type="button"
            onClick={() => {
              setEmojiOpen((o) => !o);
              setAttachMenuOpen(false);
            }}
            disabled={enviando || (estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) || sedeVencida}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              color: 'var(--color-text-secondary)',
            }}
            aria-label="Emojis"
          >
            <FiSmile size={20} />
          </button>
          {atencionInfo?.atendido_por && (
            <button
              type="button"
              onClick={() => {
                setAttachMenuOpen((o) => !o);
                setEmojiOpen(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                color: 'var(--color-text-secondary)',
              }}
              aria-label="Adjuntar"
            >
              <FiPaperclip size={20} />
            </button>
          )}

          {attachMenuOpen && atencionInfo?.atendido_por && (
            <div
              style={{
                position: 'absolute',
                bottom: '60px',
                left: '56px',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                zIndex: 50,
                minWidth: '190px',
              }}
            >
              {[
                { label: 'Galería', icon: <FiImage size={18} />, action: () => { fileInputRef.current?.click(); setAttachMenuOpen(false); } },
                { label: 'Cámara', icon: <FiCamera size={18} />, action: () => { camaraInputRef.current?.click(); setAttachMenuOpen(false); } },
                { label: 'Documento PDF', icon: <FiFileText size={18} />, action: () => { docInputRef.current?.click(); setAttachMenuOpen(false); } },
              ].map((op) => (
                <button
                  key={op.label}
                  type="button"
                  onClick={op.action}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '11px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-primary)',
                    fontSize: '13px',
                    fontWeight: 500,
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  {op.icon} {op.label}
                </button>
              ))}
            </div>
          )}

          {emojiOpen && (
            <div
              style={{
                position: 'absolute',
                bottom: '60px',
                left: '12px',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                zIndex: 50,
                padding: '8px',
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: '2px',
                width: '256px',
              }}
            >
              {EMOJIS.map((em) => (
                <button
                  key={em}
                  type="button"
                  onClick={() => {
                    setTexto((prev) => prev + em);
                    setEmojiOpen(false);
                    if (inputRef.current) inputRef.current.focus();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '4px',
                    borderRadius: '6px',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  {em}
                </button>
              ))}
            </div>
          )}
          <textarea
            ref={(el) => {
              inputRef.current = el;
            }}
            value={texto}
            onChange={(e) => {
              if (estadoOnboarding === 'ESPERANDO_UBICACION') return;
              setTexto(e.target.value);
              if (inputRef.current) {
                inputRef.current.style.height = 'auto';
                inputRef.current.style.height =
                  (inputRef.current.scrollHeight > 120 ? 120 : inputRef.current.scrollHeight) +
                  'px';
              }
            }}
            onKeyDown={(e) => {
              if (estadoOnboarding === 'ESPERANDO_UBICACION') {
                e.preventDefault();
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviarMensaje(e);
              }
            }}
            onPaste={handlePasteImagenes}
            placeholder={
              sedeVencida
                ? 'Membresía vencida — servicio no disponible'
                : estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)
                  ? 'El chat está deshabilitado en esta fase'
                  : pedidoBloqueado
                    ? 'Escribe al asesor...'
                    : placeholderOnboarding()
            }
            style={{
              ...S.input,
              opacity:
                (estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) || sedeVencida
                  ? 0.5
                  : 1,
            }}
            disabled={
              enviando ||
              estadoOnboarding === 'ESPERANDO_UBICACION' ||
              (estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) ||
              sedeVencida
            }
            autoComplete="off"
            rows={1}
          />
          {grabando ? (
            <button
              type="button"
              onClick={detenerGrabacion}
              style={{
                ...S.sendBtn,
                animation: 'pulse 1.2s infinite',
              }}
              aria-label="Detener grabacion"
            >
              <FiSquare size={16} color="#ffffff" />
            </button>
          ) : texto.trim() ? (
            <button
              type="submit"
              onClick={enviarMensaje}
              style={{
                ...S.sendBtn,
                opacity: texto.trim() && !enviando && !(estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) && !sedeVencida ? 1 : 0.4,
                cursor: texto.trim() && !enviando ? 'pointer' : 'default',
              }}
              disabled={
                !texto.trim() ||
                enviando ||
                (estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) ||
                sedeVencida
              }
              aria-label="Enviar mensaje"
            >
              <FiSend size={18} color="#ffffff" />
            </button>
          ) : !estaEnOnboarding && !(estadoPedido && ['en_camino', 'llegada'].includes(estadoPedido)) && !sedeVencida ? (
            <button
              type="button"
              onClick={iniciarGrabacion}
              disabled={enviando}
              style={{ ...S.sendBtn }}
              aria-label="Grabar audio"
            >
              <FiMic size={18} color="#ffffff" />
            </button>
          ) : (
            <button
              type="submit"
              onClick={enviarMensaje}
              style={{ ...S.sendBtn, opacity: 0.4 }}
              disabled
              aria-label="Enviar mensaje"
            >
              <FiSend size={18} color="#ffffff" />
            </button>
          )}
          {grabando && (
            <div
              style={{
                position: 'fixed',
                bottom: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--color-danger)',
                color: 'var(--color-surface)',
                padding: '10px 20px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 600,
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(234,67,53,0.4)',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-surface)',
                  animation: 'pulse 1s infinite',
                }}
              />
              Grabando... Toca el microfono para detener
            </div>
          )}
        </div>
      )}
      <style>{animationsCSS}</style>
    </div>
  );
}

export default ClienteChat;
